import useWorkspacePerms from "@/lib/useWorkspacePerms";

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { clientsLookup, brandsLookup, invalidateLookup } from "@/lib/lookupCache";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import ProjectLinkIcons from "@/components/ProjectLinkIcons";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import {
  FolderKanban, ArrowUpRight, MessageSquare, Pencil,
  List, LayoutDashboard, LayoutGrid, GanttChart, Rss, Activity, Sparkles,
  TrendingUp, TrendingDown, Clock, Plus, Loader2, ListTodo, RotateCw,
  Bell, Flag, ThumbsUp, ThumbsDown, Mic, Square, AlertTriangle,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import AdvancedTable from "@/components/table/AdvancedTable";
import { TaskSkeletonLoader } from "@/components/project/ProjectSubTabs";
import { ProjectSortChip, useProjectSort, sortProjects } from "@/components/project/FloatingProjectNavigator";
import { SmartRichTextDisplay } from "@/components/SmartRichText";
import { ProjectStatusUpdatesTab } from "@/components/project/ProjectSubTabs";
import { QuickAddTaskPopover } from "@/components/tasks/QuickAddTask";
import TaskPeekDialog from "@/components/tasks/TaskPeekDialog";
import InlinePMEditor from "@/components/project/InlinePMEditor";
import PMWorkloadPopover from "@/components/project/PMWorkloadPopover";
import MultiSelect from "@/components/ui/multi-select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useProjectStatuses, DEFAULT_STATUS_META, CANONICAL_STATUS_KEYS } from "@/hooks/useProjectStatuses";

const HEALTH_LABEL = { on_track: "On track", at_risk: "At risk", off_track: "Off track", blocked: "Blocked" };
const HEALTH_CHIP = { on_track: "chip-approved", at_risk: "chip-pending", off_track: "chip-rejected", blocked: "chip-rejected" };
const HEALTH_DOT = {
  on_track: "bg-emerald-500",
  at_risk: "bg-amber-500",
  off_track: "bg-rose-500",
  blocked: "bg-rose-700",
};
const HEALTH_BAR = {
  on_track: "bg-emerald-500",
  at_risk: "bg-amber-500",
  off_track: "bg-rose-500",
  blocked: "bg-rose-700",
};

const VIEWS = [
  { key: "list", label: "List", Icon: List },
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "gantt", label: "Gantt", Icon: GanttChart },
  { key: "timeline", label: "Timeline", Icon: Clock },
  { key: "feed", label: "Feed", Icon: Rss },
  { key: "intelligence", label: "Intelligence", Icon: Sparkles },
];

export default function Projects() {
  const { activeWorkspaceId } = useAuth();
  // Feb 2026 · Shared sort state with the project detail navigator.
  // The chip in the header lets a PM pick the review order (most
  // delayed / most unpaid / alphabetical / …) BEFORE drilling into
  // the first project; the floating navigator on the project pages
  // then cycles in exactly that order.
  const { sortKey: projectsSortKey, sortDir: projectsSortDir, setKey: setProjectsSortKey, toggleDir: toggleProjectsSortDir } = useProjectSort(activeWorkspaceId);
  const { hasPerm, modules } = useWorkspacePerms();
  // Feb 2026 · Brand module kill-switch (workspace toggle × per-role
  // perm). Hides Brand column, filter, group-by and inline picker
  // everywhere on this page.
  const brandsOn = modules?.brands !== false;
  // R159zz · Feb 2026 — Workspace custom status catalogue (rename +
  // colour). `statusMeta[key].label` overrides the built-in name and
  // `.cls` the chip colour across the filterOptions + row render.
  const { statusMeta: workspaceStatusMeta } = useProjectStatuses(activeWorkspaceId);
  const statusMeta = { ...DEFAULT_STATUS_META, ...(workspaceStatusMeta || {}) };
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);  // R31 · true until first fetch resolves
  // Feb 2026 · First-paint flag — show the shimmer skeleton loader
  // only on the very first fetch (not on sort/filter refetches) so
  // subsequent interactions never blank out the already-rendered table.
  const [projectsFirstLoad, setProjectsFirstLoad] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);   // R148 · admin-only toggle
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  // Feb 2026 R293k · Workspace-level People Labels catalogue. Loaded
  // once per workspace; each role becomes a dynamic column on the
  // Projects list via `peopleLabelColumns` below.
  const [peopleRoles, setPeopleRoles] = useState([]);
  // Iter Feb 2026 R33 · View-level RBAC. Reads the user's granular
  // `project.view.<view>` perms + falls back to admin. The stored
  // localStorage view is discarded on mount if the user no longer has
  // access to it, so they don't land on a blocked screen.
  const canView = {
    list:         hasPerm("project.view.list")         || hasPerm("workspace.admin") || hasPerm("workspace.manage") || hasPerm("project.view"),
    dashboard:    hasPerm("project.view.dashboard")    || hasPerm("workspace.admin") || hasPerm("workspace.manage"),
    gantt:        hasPerm("project.view.gantt")        || hasPerm("workspace.admin") || hasPerm("workspace.manage"),
    timeline:     hasPerm("project.view.timeline")     || hasPerm("workspace.admin") || hasPerm("workspace.manage"),
    feed:         hasPerm("project.view.feed")         || hasPerm("workspace.admin") || hasPerm("workspace.manage"),
    intelligence: hasPerm("project.view.intelligence") || hasPerm("workspace.admin") || hasPerm("workspace.manage"),
  };
  const canCreate = hasPerm("project.create") || hasPerm("workspace.admin") || hasPerm("workspace.manage");
  // Iter Feb2026 · Inline PM reassign — gate to workspace admins or PMs.
  const canEditPM = hasPerm("workspace.admin") || hasPerm("workspace.manage") || hasPerm("project.manage");
  const [view, setView] = useState(() => {
    const stored = localStorage.getItem("projects.view") || "list";
    return canView[stored] ? stored : (Object.keys(canView).find(k => canView[k]) || "list");
  });
  const [quickView, setQuickView] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);   // Feb 2026
  // Feb 2026 · Auto-open the New Project dialog when the header "+ Create"
  // shortcut passes `?new=1`. Strip the flag after consuming so a refresh
  // doesn't keep re-opening the dialog.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowNewProject(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // R66 · per-row quick actions — Add task + Peek tasks
  const [peekProject, setPeekProject] = useState(null);   // { id, name } | null
  const [quickAddProject, setQuickAddProject] = useState(null); // { id, name } | null
  // Feb 2026 R292x · Projects bulk mode — multi-select rows, then run
  // bulk status change / archive / health update from a sticky action
  // bar at the top of the table. Shares AdvancedTable's built-in
  // selectable + renderBulkBar plumbing.
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());

  const setViewPersist = (v) => { setView(v); localStorage.setItem("projects.view", v); };

  // Iter Feb 2026 R31 · Fix "Loading… never disappears" bug. Previously
  // the list-view used `loading={projects.length === 0}` which conflates
  // an empty response with an in-flight request. Now we track the
  // request lifecycle explicitly so a legitimate zero-project workspace
  // (or a user with no access to any project) sees the proper empty
  // state instead of a perpetual spinner.
  const loadProjects = () => {
    if (!activeWorkspaceId) return;
    setProjectsLoading(true);
    // Feb 2026 · Pass sort to the backend so a 500+ project workspace
    // sorts server-side via the whitelisted sort keys (open_tasks,
    // delayed_tasks, billed_tasks…).  The chip in the header is the
    // source of truth; AdvancedTable's column-click sort still runs
    // as a visual tweak on top of what the server returned.
    //
    // `/projects-with-latest` is the richer feed-ready response but
    // doesn't carry task aggregates — when the user has picked an
    // aggregate-based sort (open_tasks, delayed_tasks, …) we fetch
    // directly from `/projects` which has both the aggregates and
    // the server-side sort for them.
    const AGG_KEYS = new Set([
      "open_tasks", "total_tasks", "billable_tasks",
      "billed_tasks", "unpaid_tasks", "delayed_tasks", "overbudget_tasks",
    ]);
    const useAgg = AGG_KEYS.has(projectsSortKey);
    const qp = new URLSearchParams();
    if (includeArchived) qp.set("include_archived", "true");
    // Map `pm` (frontend chip key) → `pm_name` (backend sort key).
    const backendSortKey = projectsSortKey === "pm" ? "pm_name" : projectsSortKey;
    if (backendSortKey) qp.set("sort", backendSortKey);
    if (projectsSortDir) qp.set("dir", projectsSortDir);
    const qs = qp.toString() ? `?${qp.toString()}` : "";
    const path = useAgg
      ? `/workspaces/${activeWorkspaceId}/projects${qs}`
      : `/workspaces/${activeWorkspaceId}/projects-with-latest${qs}`;
    api.get(path)
      .then((r) => setProjects(r.data))
      .catch(() => api.get(`/workspaces/${activeWorkspaceId}/projects${qs}`).then((r) => setProjects(r.data)))
      .finally(() => { setProjectsLoading(false); setProjectsFirstLoad(false); });
  };

  useEffect(() => {
    loadProjects();
    if (activeWorkspaceId) {
      clientsLookup(activeWorkspaceId).then((rows) => setClients(rows || []));
      brandsLookup(activeWorkspaceId).then((rows) => setBrands(rows || []));
      api.get(`/workspaces/${activeWorkspaceId}/departments`).then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
      // R68 · Load workspace members so QuickAddTask can populate its Assignee dropdown.
      api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
      // Feb 2026 R293k · Load the People Labels catalogue so every
      // role appears as a column on the Projects list.
      api.get(`/workspaces/${activeWorkspaceId}/project-people-roles`)
        .then((r) => setPeopleRoles(r.data || []))
        .catch(() => setPeopleRoles([]));
    }
     
  }, [activeWorkspaceId, includeArchived, projectsSortKey, projectsSortDir]);

  // Feb 2026 fix · Apply the chip sort to the local projects array so
  // every non-list view (Gantt, Dashboard, Timeline, Feed, Intelligence)
  // honours the same order the user picked in the header. The backend
  // already sorts for the List view, but these views iterate `projects`
  // directly — without this memo they rendered in whatever insertion
  // order Mongo returned. `sortProjects` is a stable, pure sort so it
  // doesn't rerun unless the sort key / direction change.
  const sortedProjects = useMemo(
    () => sortProjects(projects, projectsSortKey, projectsSortDir),
    [projects, projectsSortKey, projectsSortDir],
  );

  const patchProjectField = async (projectId, field, value) => {
    // R150 · Feb 2026 — When flipping a project to Idle, prompt for an
    // optional reason so the PI card can explain "Why idle?" on hover.
    // Blank input = no reason (still saves the status flip).
    let idleReason = null;
    let idleReasonSet = false;
    if (field === "status" && value === "idle") {
      const current = projects.find((p) => p.id === projectId);
      if ((current?.status || "") !== "idle") {
        // eslint-disable-next-line no-alert
        const r = window.prompt(
          `Marking "${current?.name || "project"}" as Idle.\n\nWhy is this project idle? (e.g. Awaiting client feedback, budget on hold)\n\nOptional — leave blank to skip.`,
          "",
        );
        if (r !== null) { idleReason = r.trim() || null; idleReasonSet = true; }
      }
    }
    const updated = { ...projects.find((p) => p.id === projectId), [field]: value };
    if (idleReasonSet) updated.idle_reason = idleReason;
    if (field === "client_id") {
      const c = clients.find((x) => x.id === value);
      updated.client = c ? { id: c.id, name: c.name, client_code: c.client_code } : null;
      updated.client_name = c?.name;
    }
    if (field === "brand_id") {
      const b = brands.find((x) => x.id === value);
      updated.brand = b ? { id: b.id, name: b.name } : null;
      updated.brand_name = b?.name;
    }
    setProjects((prev) => prev.map((p) => p.id === projectId ? updated : p));
    try {
      const payload = { [field]: value };
      if (idleReasonSet) payload.idle_reason = idleReason;
      await api.patch(`/projects/${projectId}`, payload);
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update"));
      loadProjects();
    }
  };

  const columns = [
    // R66 · Row-level quick actions — Add task + Peek tasks. First column,
    // frozen so it stays visible during horizontal scroll. Kept narrow so
    // it doesn't push data columns off-screen.
    { key: "row_actions", label: " ", width: 78, sortable: false, filterable: false, frozen: true,
      render: (p) => (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="p-1 hover:bg-primary/10 text-primary"
            title={`Add task to ${p.name}`}
            onClick={() => setQuickAddProject({ id: p.id, name: p.name, at: Date.now() })}
            data-testid={`project-row-add-task-${p.id}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className="p-1 hover:bg-muted"
            title={`Peek tasks in ${p.name}`}
            onClick={() => setPeekProject({ id: p.id, name: p.name })}
            data-testid={`project-row-peek-tasks-${p.id}`}
          >
            <ListTodo className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      ),
    },
    { key: "name", label: "Project", width: 300, sortable: true, filterType: "text", frozen: true,
      render: (p) => (
        <div className="min-w-0 flex items-center gap-3">
          <FolderKanban className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            {canEditPM ? (
              <InlineText
                value={p.name}
                onSave={(v) => { if (v) patchProjectField(p.id, "name", v); }}
                placeholder="Untitled project"
                testid={`project-name-${p.id}`}
                className="font-medium"
              />
            ) : (
              <div className="font-medium truncate">{p.name}</div>
            )}
            {p.code && <div className="text-[10px] font-mono text-muted-foreground px-1.5">{p.code}</div>}
          </div>
        </div>
      ),
    },
    { key: "code", label: "Code", width: 120, sortable: true, filterType: "text",
      render: (p) => (
        <InlineText
          value={p.code}
          onSave={(v) => patchProjectField(p.id, "code", v || null)}
          placeholder="—"
          testid={`project-code-${p.id}`}
        />
      ),
    },
    { key: "client_name", label: "Client", width: 220, sortable: true, groupable: true, filterType: "text",
      accessor: (p) => p.client?.name || p.client_name || "",
      render: (p) => (
        <InlinePicker
          value={p.client_id}
          currentLabel={p.client?.name || p.client_name}
          options={clients.map((c) => ({ value: c.id, label: c.client_code ? `${c.name} · ${c.client_code}` : c.name }))}
          onSave={(v) => patchProjectField(p.id, "client_id", v)}
          testid={`project-client-${p.id}`}
          placeholder="— Assign client —"
        />
      ),
    },
    { key: "brand_name", label: "Brand", width: 200, sortable: true, groupable: true, filterType: "text",
      accessor: (p) => p.brand?.name || p.brand_name || "",
      render: (p) => (
        <InlinePicker
          value={p.brand_id}
          currentLabel={p.brand?.name || p.brand_name}
          options={brands
            .filter((b) => !p.client_id || !b.client_id || b.client_id === p.client_id)
            .map((b) => ({ value: b.id, label: b.name }))}
          onSave={(v) => patchProjectField(p.id, "brand_id", v)}
          onClear={() => patchProjectField(p.id, "brand_id", "")}
          allowClear
          testid={`project-brand-${p.id}`}
          placeholder="— No brand —"
        />
      ),
    },
    // R268iiii · Feb 2026 — Department column with inline picker.
    { key: "department_name", label: "Department", width: 180, sortable: true, groupable: true, filterType: "text",
      accessor: (p) => p.department?.name || p.department_name || "",
      render: (p) => (
        <InlinePicker
          value={p.department_id}
          currentLabel={p.department?.name || p.department_name}
          options={departments.map((d) => ({ value: d.id, label: d.code ? `${d.name} · ${d.code}` : d.name }))}
          onSave={(v) => patchProjectField(p.id, "department_id", v)}
          onClear={() => patchProjectField(p.id, "department_id", "")}
          allowClear
          testid={`project-department-${p.id}`}
          placeholder={departments.length === 0 ? "— No departments —" : "— Assign department —"}
        />
      ),
    },
    { key: "health", label: "Health", width: 140, groupable: true, filterType: "select",
      filterOptions: Object.entries(HEALTH_LABEL).map(([v, l]) => ({ value: v, label: l })),
      render: (p) => <HealthCell health={p.health} />,
      exportValue: (p) => HEALTH_LABEL[p.health || "on_track"],
    },
    // R68 · Session A · Project execution status. New extended set
    // includes Hold / Paused / Awaiting Work / Inactive — these four
    // are pause-states that suppress the daily PI nudge (see backend
    // R68 hotfix — projects in these statuses only get a weekly ping).
    { key: "status", label: "Status", width: 150, groupable: true, filterType: "select",
      filterOptions: CANONICAL_STATUS_KEYS.map((k) => ({ value: k, label: statusMeta[k]?.label || k })),
      render: (p) => (
        <InlineStatus
          project={p}
          statusMeta={statusMeta}
          canEdit={canEditPM}
          onSave={(v) => patchProjectField(p.id, "status", v)}
          testid={`project-status-${p.id}`}
        />
      ),
      exportValue: (p) => p.status || "active",
    },
    // Iter Feb2026 · Consolidated "Project Manager" column — the earlier
    // Owner column now merges into this since agencies use them
    // interchangeably. Falls back through the chain so legacy rows keep
    // showing a name. Groupable so the list can be grouped by PM.
    // Inline edit via popover — click cell to reassign without opening
    // the detail page.
    { key: "pm_name", label: "Project Manager", width: 180, sortable: true,
      groupable: true, filterType: "text",
      sortValue: (p) => (p.pm_name || p.project_manager_name || p.owner_name || p.created_by_name || "").toLowerCase(),
      accessor: (p) => p.pm_name || p.project_manager_name || p.owner_name || p.created_by_name || "",
      render: (p) => (
        <InlinePMEditor
          project={p}
          members={members}
          canEdit={canEditPM}
          onSaved={(updated) => setProjects((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x))}
        />
      ),
      exportValue: (p) => p.pm_name || p.project_manager_name || p.owner_name || p.created_by_name || "",
    },
    // R121 · Feb 2026 — Open task count column. `task_counts` is already
    // computed by the backend (used elsewhere for the feed card); read
    // the "open" bucket which excludes done + cancelled.
    { key: "open_tasks", label: "Open tasks", width: 110, sortable: true, filterType: "number",
      sortValue: (p) => (p.task_counts?.open || 0) + (p.task_counts?.in_progress || 0),
      render: (p) => {
        const open = p.task_counts?.open || 0;
        const inProg = p.task_counts?.in_progress || 0;
        const total = open + inProg;
        return (
          <span className="font-mono text-xs" title={`${open} open · ${inProg} in progress`} data-testid={`proj-open-tasks-${p.id}`}>
            {total > 0 ? total : "—"}
            {inProg > 0 && <span className="ml-1 text-[10px] text-emerald-700">({inProg} ▶)</span>}
          </span>
        );
      },
      exportValue: (p) => (p.task_counts?.open || 0) + (p.task_counts?.in_progress || 0),
    },
    {
      // Feb 2026 · Delayed tasks column — surfaces the new
      // backend aggregate `delayed_tasks` so PMs can scan "which
      // projects have the most overdue work" at a glance. Cell is
      // tinted when the count is non-zero so the eye snaps to it.
      key: "delayed_tasks", label: "Delayed", width: 100, sortable: true, filterType: "number",
      sortValue: (p) => Number(p.delayed_tasks || 0),
      render: (p) => {
        const n = Number(p.delayed_tasks || 0);
        return (
          <span className={`font-mono text-xs inline-flex items-center gap-1 ${n > 0 ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}
                title={n > 0 ? `${n} task${n === 1 ? "" : "s"} past their due date (status not done)` : "No delayed tasks"}
                data-testid={`proj-delayed-tasks-${p.id}`}>
            {n > 0 ? <AlertTriangle className="w-3 h-3" /> : null}
            {n > 0 ? n : "—"}
          </span>
        );
      },
      exportValue: (p) => Number(p.delayed_tasks || 0),
    },
    {
      // Feb 2026 · Sum of (today − due_date) across all delayed tasks
      // in the project. Lets a PM see magnitude at a glance — "7
      // tasks slipped by an average 9 days" (63) is very different
      // from "10 tasks slipped by 2 days each" (20) even though the
      // Delayed count is the same order of magnitude.
      key: "delayed_days_total", label: "Days delayed", width: 110, sortable: true, filterType: "number",
      sortValue: (p) => Number(p.delayed_days_total || 0),
      render: (p) => {
        const n = Number(p.delayed_days_total || 0);
        const dc = Number(p.delayed_tasks || 0);
        const avg = dc > 0 ? Math.round((n / dc) * 10) / 10 : 0;
        return (
          <span className={`font-mono text-xs ${n > 0 ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}
                title={n > 0 ? `${n} day-slip across ${dc} task${dc === 1 ? "" : "s"} · avg ${avg} day${avg === 1 ? "" : "s"} each` : "On schedule"}
                data-testid={`proj-delayed-days-${p.id}`}>
            {n > 0 ? `${n}d` : "—"}
          </span>
        );
      },
      exportValue: (p) => Number(p.delayed_days_total || 0),
    },
    { key: "latest_update_at", label: "Last update", width: 130, sortable: true, filterType: "date-range",
      // Sorts on latest_update.created_at while the cell prints the
      // relative time + author to match the Feed vocabulary.
      accessor: (p) => p.latest_update?.created_at || p.updated_at || "",
      render: (p) => {
        const at = p.latest_update?.created_at;
        if (!at) return <span className="text-[11px] text-muted-foreground">—</span>;
        return (
          <div className="min-w-0">
            <div className="text-xs">{fmtRelative(at)}</div>
            <div className="text-[10px] text-muted-foreground truncate">{p.latest_update?.author_name || ""}</div>
          </div>
        );
      },
      exportValue: (p) => p.latest_update?.created_at || "",
    },
    { key: "planned_start_date", label: "Planned start", width: 130, sortable: true, filterType: "date-range",
      render: (p) => (
        <InlineDate
          value={(p.planned_start_date || p.start_date || "").slice(0, 10)}
          canEdit={canEditPM}
          onSave={(v) => patchProjectField(p.id, "planned_start_date", v || null)}
          testid={`project-planned-start-${p.id}`}
        />
      ),
      accessor: (p) => p.planned_start_date || p.start_date || "",
    },
    { key: "planned_end_date", label: "Planned end", width: 130, sortable: true, filterType: "date-range",
      render: (p) => (
        <InlineDate
          value={(p.planned_end_date || p.end_date || "").slice(0, 10)}
          canEdit={canEditPM}
          onSave={(v) => patchProjectField(p.id, "planned_end_date", v || null)}
          testid={`project-planned-end-${p.id}`}
        />
      ),
      accessor: (p) => p.planned_end_date || p.end_date || "",
    },
    { key: "actual_start_date", label: "Actual start", width: 130, sortable: true, filterType: "date-range", visible: false,
      render: (p) => (
        <InlineDate
          value={(p.actual_start_date || "").slice(0, 10)}
          canEdit={canEditPM}
          onSave={(v) => patchProjectField(p.id, "actual_start_date", v || null)}
          testid={`project-actual-start-${p.id}`}
        />
      ),
    },
    { key: "actual_end_date", label: "Actual end", width: 130, sortable: true, filterType: "date-range", visible: false,
      render: (p) => (
        <InlineDate
          value={(p.actual_end_date || "").slice(0, 10)}
          canEdit={canEditPM}
          onSave={(v) => patchProjectField(p.id, "actual_end_date", v || null)}
          testid={`project-actual-end-${p.id}`}
        />
      ),
    },
    { key: "estimated_hours", label: "Est. hrs", width: 90, sortable: true, align: "right", type: "number", sum: true,
      render: (p) => <span className="font-mono text-xs">{p.estimated_hours ?? "—"}</span>,
    },
    { key: "actual_hours", label: "Actual hrs", width: 90, sortable: true, align: "right", type: "number", sum: true, visible: false,
      render: (p) => <span className="font-mono text-xs">{p.actual_hours ?? "—"}</span>,
    },
    { key: "manual_effort_hours", label: "Manual effort (h)", width: 130, sortable: true, align: "right", type: "number", sum: true,
      render: (p) => <span className="font-mono text-xs" title={p.manual_effort_note || ""}>{p.project_manual_effort_hours ?? p.manual_effort_hours ?? 0}{p.manual_effort_note ? " •" : ""}</span>,
    },
    { key: "timesheet_hours", label: "Timesheet (h)", width: 110, sortable: true, align: "right", type: "number", sum: true,
      render: (p) => <span className="font-mono text-xs text-primary">{p.timesheet_hours ?? 0}</span>,
    },
    { key: "total_effort_hours", label: "Total effort (h)", width: 120, sortable: true, align: "right", type: "number", sum: true,
      render: (p) => <span className="font-mono text-xs font-semibold">{p.total_effort_hours ?? 0}</span>,
    },
    { key: "review_item_count", label: "Items", width: 80, sortable: true, align: "right", type: "number", sum: true, filterType: "number-range" },
    { key: "latest_update", label: "Latest update", width: 260, sortable: false,
      accessor: (p) => p.latest_update?.body_preview || "",
      render: (p) => p.latest_update ? (
        // Iter Feb 2026 R35 · Hover-tooltip shows the full comment text so
        // long updates aren't lost behind the 140-char preview.
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0 cursor-help" data-testid={`project-latest-update-${p.id}`}>
                <div className="text-xs truncate">{p.latest_update.body_preview}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {p.latest_update.author_name} · {fmtRelative(p.latest_update.created_at)}
                  <span className={`chip text-[9px] ml-1 ${p.latest_update.visibility === "client" ? "chip-approved" : ""}`}>{p.latest_update.visibility}</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="top" align="start" sideOffset={6}
              className="max-w-md bg-background text-foreground border border-border shadow-lg px-3 py-2 whitespace-pre-wrap text-xs leading-relaxed"
              data-testid={`project-latest-update-tooltip-${p.id}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {p.latest_update.author_name || "Update"} · {fmtRelative(p.latest_update.created_at)}
              </div>
              <div className="max-h-64 overflow-y-auto pr-1">
                {p.latest_update.body_full || p.latest_update.body_preview}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : <span className="text-xs text-foreground/40">—</span>,
    },
    { key: "quick_view", label: "Updates", width: 100, sortable: false, align: "center",
      render: (p) => (
        <button type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(p); }}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-border hover:bg-muted"
          data-testid={`project-quick-view-${p.id}`}
          title="Quick view: status updates">
          <MessageSquare className="w-3 h-3" /> {p.status_updates_count || 0}
        </button>
      ),
    },
    { key: "updated_by_name", label: "Updated by", width: 140, sortable: true, filterType: "text",
      render: (p) => (
        <div className="min-w-0">
          <div className="text-xs truncate">{p.updated_by_name || "—"}</div>
          <div className="text-[10px] text-muted-foreground">{p.updated_at ? fmtRelative(p.updated_at) : ""}</div>
        </div>
      ),
      exportValue: (p) => p.updated_by_name || "",
    },
    { key: "created_at", label: "Created", width: 120, sortable: true, align: "right", filterType: "date-range", visible: false,
      render: (p) => <span className="text-xs text-muted-foreground">{fmtRelative(p.created_at)}</span>,
    },
    // Feb 2026 R293n · Project Links column — service icons with hover
    // tooltip for every quick-link saved on the project (WhatsApp,
    // Drive, Figma, etc.). Clicking an icon opens in a new tab.
    {
      key: "project_links",
      label: "Links",
      width: 140,
      sortable: false,
      exportValue: (p) => (p.project_links || []).map((l) => `${l.label}: ${l.url}`).join("; "),
      render: (p) => (
        <ProjectLinkIcons
          links={p.project_links || []}
          max={4}
          size="xs"
          testidBase={`proj-row-link-${p.id}`}
        />
      ),
    },
    // Feb 2026 R293k · Dynamic People Label columns — one per entry in
    // the workspace catalogue. Appended at the end so they don't push
    // the primary columns off-screen on first paint.
    ...peopleRoles.map((role) => ({
      key: `people_label_${role.id}`,
      label: role.name,
      width: 180,
      sortable: true,
      filterType: "text",
      /* People Label columns are new; keep them hidden by default so a
         workspace with many labels doesn't drown the primary columns
         off-screen on first paint. Admins toggle them from the
         AdvancedTable column picker just like every other column. */
      visible: false,
      sortValue: (p) => {
        const uids = p.people_labels?.[role.id] || [];
        return uids
          .map((uid) => {
            const m = members.find((x) => (x.user_id || x.id) === uid);
            return m?.name || m?.email || m?.user?.name || uid;
          })
          .join(", ")
          .toLowerCase();
      },
      accessor: (p) => {
        const uids = p.people_labels?.[role.id] || [];
        return uids
          .map((uid) => {
            const m = members.find((x) => (x.user_id || x.id) === uid);
            return m?.name || m?.email || m?.user?.name || "User";
          })
          .join(", ");
      },
      render: (p) => {
        const uids = p.people_labels?.[role.id] || [];
        if (!canEditPM) {
          if (!uids.length) return <span className="text-muted-foreground text-xs">—</span>;
          const resolved = uids.map((uid) => {
            const m = members.find((x) => (x.user_id || x.id) === uid);
            const name = m?.name || m?.email || m?.user?.name || "User";
            const initials = name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
            return { uid, name, initials };
          });
          const extra = resolved.length > 3 ? resolved.length - 3 : 0;
          return (
            <div className="flex items-center gap-1 flex-wrap" data-testid={`proj-people-${role.id}-${p.id}`}>
              {resolved.slice(0, 3).map((r) => (
                <span key={r.uid}
                       title={r.name}
                       className={`inline-flex items-center gap-1 text-[10px] border px-1.5 py-0.5 ${role.color || "bg-muted/40 border-border"}`}>
                  <span className="font-semibold">{r.initials}</span>
                  <span className="truncate max-w-[12ch]">{r.name}</span>
                </span>
              ))}
              {extra > 0 && (
                <span className="text-[10px] border border-border px-1.5 py-0.5 bg-muted text-muted-foreground"
                       title={resolved.slice(3).map((r) => r.name).join(", ")}>
                  +{extra}
                </span>
              )}
            </div>
          );
        }
        return (
          <InlinePeopleLabel
            project={p}
            role={role}
            members={members}
            onSaved={(updated) => setProjects((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x))}
            testid={`proj-people-${role.id}-${p.id}`}
          />
        );
      },
      exportValue: (p) => {
        const uids = p.people_labels?.[role.id] || [];
        return uids
          .map((uid) => {
            const m = members.find((x) => (x.user_id || x.id) === uid);
            return m?.name || m?.email || m?.user?.name || "";
          })
          .filter(Boolean)
          .join(", ");
      },
    })),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Projects"
        title="All projects"
        density="dense"
        description="Across all clients in this workspace."
        testid="projects-header"
        actions={
          <div className="flex items-center gap-2">
            <ProjectSortChip
              sortKey={projectsSortKey}
              sortDir={projectsSortDir}
              onChangeKey={setProjectsSortKey}
              onToggleDir={toggleProjectsSortDir}
              testid="projects-list-sort-chip"
            />
            {(hasPerm("workspace.admin") || hasPerm("workspace.manage") || hasPerm("project.archive")) && (
              <label
                className="flex items-center gap-1.5 text-xs px-2 py-1 border border-border cursor-pointer select-none"
                title="Show archived projects (admin only)"
                data-testid="projects-include-archived-toggle"
              >
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  data-testid="projects-include-archived-checkbox"
                />
                Include archived
              </label>
            )}
            <ViewSwitcher current={view} onChange={setViewPersist} allowed={canView} />
            {canCreate && (
              <Button className="rounded-none" onClick={() => setShowNewProject(true)} data-testid="projects-new-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> New project
              </Button>
            )}
          </div>
        }
      />
      {showNewProject && (
        <NewProjectDialog
          clients={clients}
          brands={brands}
          departments={departments}
          onClose={() => setShowNewProject(false)}
          onCreated={(p) => {
            setShowNewProject(false);
            toast.success("Project created");
            loadProjects();
            if (p?.id) navigate(`/app/projects/${p.id}`);
          }}
        />
      )}
      <div className="px-4 sm:px-6 md:px-10 py-8" data-testid={`projects-view-${view}`}>
        {view === "list" && projectsFirstLoad ? (
          <TaskSkeletonLoader rows={12} />
        ) : view === "list" && (
          <AdvancedTable
            tableKey="projects"
            title="Projects"
            rows={sortedProjects}
            columns={brandsOn ? columns : columns.filter((c) => c.key !== "brand_name")}
            loading={projectsLoading}
            emptyIcon={FolderKanban}
            emptyTitle={projects.length === 0 && !projectsLoading ? "No projects visible" : "No projects yet"}
            emptyDescription="Either no projects have been created yet, or you don't have access to any project in this workspace. Ask a workspace admin to add you as a project member."
            onRowClick={(p) => navigate(`/app/projects/${p.id}`)}
            selectable={true}
            selectedIds={selectedProjectIds}
            onSelectionChange={setSelectedProjectIds}
            renderBulkBar={(rows) => (
              <ProjectsBulkActions
                rows={rows}
                statusMeta={statusMeta}
                wsId={activeWorkspaceId}
                clients={clients}
                brands={brands}
                departments={departments}
                members={members}
                onClear={() => setSelectedProjectIds(new Set())}
                onDone={() => { setSelectedProjectIds(new Set()); loadProjects(); }}
              />
            )}
            groupSummary={(items, key, gb) => {
              // Iter Feb2026 · Only show a workload bar when grouped by PM.
              if (gb !== "pm_name") return null;
              let tasks = 0, open = 0, overdue = 0;
              const today = new Date().toISOString().slice(0, 10);
              const firstPmId = items.find((p) => p.pm_id)?.pm_id
                                 || items.find((p) => p.owner_id)?.owner_id
                                 || null;
              for (const p of items) {
                const tc = p.task_counts || {};
                const t = (tc.open || 0) + (tc.in_progress || 0) + (tc.done || 0) + (tc.blocked || 0);
                tasks += t;
                open += (tc.open || 0) + (tc.in_progress || 0);
                const mf = p.milestone_flag || {};
                overdue += (mf.overdue || 0);
                if (p.status && p.status !== "completed" && p.planned_end_date && p.planned_end_date < today) {
                  overdue += 1;
                }
              }
              const load = tasks === 0 ? 0 : Math.min(100, Math.round((open / Math.max(1, tasks)) * 100));
              const barColor = load > 66 ? "bg-rose-500"
                : load > 33 ? "bg-amber-500"
                : "bg-emerald-500";
              return (
                <PMWorkloadPopover
                  wsId={activeWorkspaceId}
                  pmName={key}
                  pmUserId={firstPmId}
                  fallbackProjects={items}
                  fallbackOpen={open}
                  fallbackOverdue={overdue}>
                  <span className="inline-flex items-center gap-2 text-[10px] font-normal text-muted-foreground cursor-pointer hover:text-foreground"
                        data-testid="projects-group-pm-workload"
                        title="Click to see this PM's project breakdown">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-24 h-1.5 bg-muted overflow-hidden border border-border/40">
                        <span className={`block h-full ${barColor}`} style={{ width: `${load}%` }} />
                      </span>
                      <span className="font-mono tabular-nums">{load}%</span>
                    </span>
                    <span className="font-mono tabular-nums">{open}<span className="opacity-60"> open</span></span>
                    {overdue > 0 && (
                      <span className="font-mono tabular-nums px-1 border border-rose-500/40 bg-rose-500/10 text-rose-700">
                        ⚠ {overdue} overdue
                      </span>
                    )}
                    <span className="opacity-60">· {items.length} project{items.length === 1 ? "" : "s"}</span>
                  </span>
                </PMWorkloadPopover>
              );
            }}
          />
        )}
        {view !== "list" && !projectsLoading && projects.length === 0 && (
          <EmptyState
            title="No projects visible"
            description="Either no projects have been created yet, or you don't have access to any project in this workspace. Ask a workspace admin to add you as a project member."
            icon={FolderKanban}
            testid="projects-empty"
            action={<Link to="/app/clients"><Button className="rounded-none" data-testid="projects-empty-cta">Go to clients</Button></Link>}
          />
        )}
        {view !== "list" && projectsLoading && (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="projects-loading">
            Loading projects…
          </div>
        )}
        {view === "dashboard" && !projectsLoading && projects.length > 0 && <DashboardView projects={sortedProjects} />}
        {view === "gantt" && !projectsLoading && projects.length > 0 && <GanttView projects={sortedProjects} onPatch={patchProjectField} />}
        {view === "timeline" && !projectsLoading && projects.length > 0 && <TimelineView projects={sortedProjects} />}
        {view === "feed" && !projectsLoading && projects.length > 0 && <FeedView projects={sortedProjects} wsId={activeWorkspaceId} members={members} onRefresh={loadProjects} />}
        {view === "intelligence" && !projectsLoading && projects.length > 0 && <IntelligenceView projects={sortedProjects} />}
      </div>
      {quickView && (
        <Dialog open onOpenChange={(v) => { if (!v) setQuickView(null); }}>
          <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="project-quick-view-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> {quickView.name} — updates</span>
                <Link to={`/app/projects/${quickView.id}`} onClick={() => setQuickView(null)} className="text-xs text-primary hover:underline inline-flex items-center gap-1">Open project <ArrowUpRight className="w-3 h-3" /></Link>
              </DialogTitle>
            </DialogHeader>
            <ProjectStatusUpdatesTab projectId={quickView.id} project={quickView} projectMembers={[]} />
          </DialogContent>
        </Dialog>
      )}
      {/* R66 · Task Peek popup — reused on the Portfolio Detail too. */}
      <TaskPeekDialog
        open={!!peekProject}
        onOpenChange={(o) => !o && setPeekProject(null)}
        wsId={activeWorkspaceId}
        projectId={peekProject?.id}
        projectName={peekProject?.name}
        onAddTask={() => setQuickAddProject({ ...peekProject, at: Date.now() })}
      />
      {/* R66/R68 · Row-scoped Quick Add — no visible trigger → dialog mode,
          full members list for Assignee. R68 · Don't loadProjects() on
          create (that reset any active filter/sort); the newly created
          task is a task, not a project, so no visible project row changes
          need to happen. */}
      {quickAddProject && (
        <QuickAddTaskPopover
          key={quickAddProject.id + ":" + quickAddProject.at}
          wsId={activeWorkspaceId}
          preset={{ project_id: quickAddProject.id }}
          projects={projects}
          members={members}
          defaultOpen
          onOpenChange={(o) => { if (!o) setQuickAddProject(null); }}
          onCreated={() => { setQuickAddProject(null); }}
          testid={`project-row-quick-add-${quickAddProject.id}`}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// View switcher
// ═══════════════════════════════════════════════════════════════
function ViewSwitcher({ current, onChange, allowed }) {
  // R33 · Show only views the user is allowed to see.
  const visibleViews = allowed
    ? VIEWS.filter(v => allowed[v.key])
    : VIEWS;
  return (
    <div className="inline-flex border border-border" data-testid="projects-view-switcher">
      {visibleViews.map(({ key, label, Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border-r border-border last:border-r-0 transition ${current === key ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground hover:bg-muted"}`}
          data-testid={`projects-view-btn-${key}`}
          title={label}
        >
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden lg:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Inline picker for client / brand
// ═══════════════════════════════════════════════════════════════
function InlinePicker({ value, currentLabel, options, onSave, onClear, allowClear, testid, placeholder }) {
  const [editing, setEditing] = useState(false);
  const sorted = (options || []).slice().sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  if (editing) {
    return (
      <div className="min-w-[180px] flex items-center gap-1">
        <SearchableSelect
          value={value || ""}
          onValueChange={(v) => { if (v && v !== value) onSave(v); setEditing(false); }}
          options={sorted}
          placeholder={placeholder || "— Select —"}
          data-testid={`${testid}-select`}
          width="100%"
          triggerClassName="h-7 px-2 py-1 text-xs rounded-none border-primary"
        />
        {allowClear && value && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onClear && onClear(); setEditing(false); }} className="text-[10px] text-muted-foreground hover:text-rose-500 whitespace-nowrap" data-testid={`${testid}-clear`}>Clear</button>
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="group inline-flex items-center gap-1 text-xs text-left w-full hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border"
      data-testid={testid}
    >
      <span className={`truncate ${currentLabel ? "" : "text-muted-foreground italic"}`}>{currentLabel || placeholder}</span>
      <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition ml-auto shrink-0" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Health cell — dot + label + tiny bar
// ═══════════════════════════════════════════════════════════════
function HealthCell({ health }) {
  const h = health || "on_track";
  const bar = HEALTH_BAR[h] || "bg-muted";
  const dot = HEALTH_DOT[h] || "bg-muted";
  return (
    <div className="flex items-center gap-2" title={HEALTH_LABEL[h]}>
      <span className={`w-2 h-2 rounded-full ${dot} shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className={`chip text-[10px] ${HEALTH_CHIP[h] || ""} inline-block`}>{HEALTH_LABEL[h] || h}</div>
        <div className="h-0.5 mt-1 bg-muted overflow-hidden">
          <div className={`h-full ${bar}`} style={{ width: h === "on_track" ? "100%" : h === "at_risk" ? "60%" : h === "off_track" ? "35%" : "15%" }} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Dashboard view — KPI tiles + at-risk callout
// ═══════════════════════════════════════════════════════════════
function DashboardView({ projects }) {
  const stats = useMemo(() => {
    const counts = { on_track: 0, at_risk: 0, off_track: 0, blocked: 0 };
    let totalEst = 0, totalActual = 0, overdue = 0;
    const today = new Date().toISOString().slice(0, 10);
    projects.forEach((p) => {
      counts[p.health || "on_track"] = (counts[p.health || "on_track"] || 0) + 1;
      totalEst += Number(p.estimated_hours || 0);
      totalActual += Number(p.total_effort_hours || 0);
      const end = p.planned_end_date || p.end_date;
      if (end && end < today && p.status !== "completed" && p.status !== "closed") overdue += 1;
    });
    return { total: projects.length, counts, totalEst, totalActual, overdue };
  }, [projects]);

  const atRisk = projects.filter((p) => ["at_risk", "off_track", "blocked"].includes(p.health)).slice(0, 8);
  const util = stats.totalEst > 0 ? Math.round((stats.totalActual / stats.totalEst) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="projects-dashboard-view">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI title="Total projects" value={stats.total} sub="all statuses" />
        <KPI title="On track" value={stats.counts.on_track} sub={pct(stats.counts.on_track, stats.total)} tone="emerald" />
        <KPI title="At risk" value={stats.counts.at_risk} sub={pct(stats.counts.at_risk, stats.total)} tone="amber" />
        <KPI title="Off track" value={stats.counts.off_track + stats.counts.blocked} sub={pct(stats.counts.off_track + stats.counts.blocked, stats.total)} tone="rose" />
        <KPI title="Overdue" value={stats.overdue} sub="past planned end" tone={stats.overdue > 0 ? "rose" : "slate"} />
        <KPI title="Utilization" value={`${util}%`} sub={`${Math.round(stats.totalActual)}h / ${Math.round(stats.totalEst)}h`} tone={util > 100 ? "rose" : util > 80 ? "amber" : "emerald"} />
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Needs attention · {atRisk.length}</h3>
        {atRisk.length === 0 ? (
          <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No at-risk projects. Nice.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2">
            {atRisk.map((p) => (
              <Link key={p.id} to={`/app/projects/${p.id}`} className="border border-border p-3 hover:border-primary/40 flex items-center gap-3" data-testid={`dashboard-at-risk-${p.id}`}>
                <HealthCell health={p.health} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {p.client?.name || p.client_name || "—"}
                    {p.brand?.name && <span> · {p.brand.name}</span>}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ title, value, sub, tone = "slate" }) {
  const toneMap = {
    slate: "border-border",
    emerald: "border-emerald-500/50 bg-emerald-500/5",
    amber: "border-amber-500/50 bg-amber-500/5",
    rose: "border-rose-500/50 bg-rose-500/5",
  };
  return (
    <div className={`border ${toneMap[tone] || toneMap.slate} p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function pct(n, total) { return total ? `${Math.round(n / total * 100)}%` : "—"; }

// ═══════════════════════════════════════════════════════════════
// Gantt view — simple SVG-free horizontal bar rows with drag-to-reschedule
// ═══════════════════════════════════════════════════════════════
function GanttView({ projects, onPatch }) {
  // R96 · Search bar — filter dense timelines by project name / client name.
  const [q, setQ] = useState("");
  const qq = q.trim().toLowerCase();
  const rows = projects
    .map((p) => ({
      ...p,
      start: p.planned_start_date || p.actual_start_date || p.start_date,
      end: p.planned_end_date || p.actual_end_date || p.end_date,
    }))
    .filter((p) => p.start && p.end)
    .filter((p) => !qq
      || (p.name || "").toLowerCase().includes(qq)
      || (p.client?.name || p.client_name || "").toLowerCase().includes(qq));

  if (rows.length === 0) {
    return (
      <div className="space-y-3" data-testid="projects-gantt-view">
        <GanttSearchBar q={q} setQ={setQ} count={0} />
        <EmptyState
          title={qq ? "No matches" : "No dated projects"}
          description={qq
            ? "Try a different search — nothing matches this filter."
            : "Set planned start + end dates to see the Gantt view."}
          icon={GanttChart}
          testid="projects-gantt-empty"
        />
      </div>
    );
  }
  const minMs = Math.min(...rows.map((r) => new Date(r.start).getTime()));
  const maxMs = Math.max(...rows.map((r) => new Date(r.end).getTime()));
  const span = Math.max(1, maxMs - minMs);
  const today = Date.now();
  const monthMarkers = buildMonthMarkers(new Date(minMs), new Date(maxMs));

  return (
    <div className="space-y-2 overflow-x-auto" data-testid="projects-gantt-view">
      <GanttSearchBar q={q} setQ={setQ} count={rows.length} />
      <div className="text-[11px] text-muted-foreground mb-2 flex items-center gap-3">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500" /> On track</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500" /> At risk</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-rose-500" /> Off track</span>
        <span className="ml-4">💡 <b>Drag a bar left/right</b> to reschedule (both start + end move together). Changes save automatically.</span>
      </div>
      <div className="relative border border-border">
        <div className="h-8 border-b border-border bg-muted/30 relative">
          {monthMarkers.map((m) => (
            <div key={m.label} className="absolute top-0 h-full border-l border-border/70 pl-1 text-[10px] text-muted-foreground pt-1" style={{ left: `calc(224px + ${((m.ms - minMs) / span) * 100}% * 0.999)` }}>
              {m.label}
            </div>
          ))}
        </div>
        <div>
          {rows.map((p) => (
            <GanttRow key={p.id} p={p} minMs={minMs} span={span} today={today} onPatch={onPatch} />
          ))}
        </div>
      </div>
    </div>
  );
}

function GanttSearchBar({ q, setQ, count }) {
  return (
    <div className="flex items-center gap-2" data-testid="gantt-search-bar">
      <div className="relative w-full sm:w-80">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Gantt by project or client name…"
          className="w-full h-8 pl-8 pr-3 text-xs border border-border rounded-none bg-background focus:outline-none focus:border-primary"
          data-testid="gantt-search-input"
        />
        <svg
          className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid="gantt-search-clear"
          >
            ×
          </button>
        )}
      </div>
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="gantt-search-count">
        {count} match{count === 1 ? "" : "es"}
      </span>
    </div>
  );
}

function GanttRow({ p, minMs, span, today, onPatch }) {
  const s = new Date(p.start).getTime();
  const e = new Date(p.end).getTime();
  const durMs = Math.max(1, e - s);
  const [dragOffsetMs, setDragOffsetMs] = useState(0);
  const [saving, setSaving] = useState(false);
  const rowRef = (typeof window !== "undefined") ? window : null;

  const displayS = s + dragOffsetMs;
  const displayE = e + dragOffsetMs;
  const left = ((displayS - minMs) / span) * 100;
  const width = Math.max(1, (durMs / span) * 100);
  const bar = HEALTH_BAR[p.health] || "bg-primary";

  const startDrag = (evt) => {
    evt.preventDefault(); evt.stopPropagation();
    const startX = evt.clientX;
    const track = evt.currentTarget.parentElement;
    const trackWidth = track?.getBoundingClientRect().width || 1;
    const msPerPx = span / trackWidth;
    const onMove = (mv) => setDragOffsetMs((mv.clientX - startX) * msPerPx);
    const onUp = async (mv) => {
      rowRef.removeEventListener("pointermove", onMove);
      rowRef.removeEventListener("pointerup", onUp);
      const finalOffset = (mv.clientX - startX) * msPerPx;
      if (Math.abs(finalOffset) < 24 * 3600 * 1000) { setDragOffsetMs(0); return; }
      const newStart = new Date(s + finalOffset).toISOString().slice(0, 10);
      const newEnd = new Date(e + finalOffset).toISOString().slice(0, 10);
      setSaving(true);
      try {
        await onPatch(p.id, "planned_start_date", newStart);
        await onPatch(p.id, "planned_end_date", newEnd);
      } finally {
        setSaving(false); setDragOffsetMs(0);
      }
    };
    rowRef.addEventListener("pointermove", onMove);
    rowRef.addEventListener("pointerup", onUp, { once: true });
  };

  return (
    <Link
      to={`/app/projects/${p.id}`}
      onClick={(e) => { if (dragOffsetMs !== 0) e.preventDefault(); }}
      className="relative h-9 border-b border-border hover:bg-muted/20 flex items-center px-2 group"
      data-testid={`gantt-row-${p.id}`}
    >
      <div className="w-56 min-w-56 truncate text-xs pr-2 z-10 bg-background/95 group-hover:bg-muted/40 flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[p.health] || "bg-slate-400"} shrink-0`} />
        <span className="truncate font-medium">{p.name}</span>
        {saving && <span className="text-[9px] text-muted-foreground">saving…</span>}
      </div>
      <div className="flex-1 relative h-full">
        <div
          className={`absolute top-1.5 bottom-1.5 ${bar} rounded-sm shadow-sm cursor-grab active:cursor-grabbing select-none ${dragOffsetMs !== 0 ? "ring-2 ring-primary ring-offset-1" : ""}`}
          style={{ left: `${left}%`, width: `${width}%` }}
          title={`${new Date(displayS).toISOString().slice(0, 10)} → ${new Date(displayE).toISOString().slice(0, 10)}${dragOffsetMs !== 0 ? " (dragging — release to save)" : ""}`}
          onPointerDown={startDrag}
          data-testid={`gantt-bar-${p.id}`}
        >
          <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-medium truncate px-1 pointer-events-none">
            {p.client?.name || p.client_name || ""}
          </span>
        </div>
        {today > s && today < e && (
          <div className="absolute top-0 h-full w-px bg-rose-500 z-10 pointer-events-none" style={{ left: `${((today - minMs) / span) * 100}%` }} />
        )}
      </div>
    </Link>
  );
}

function buildMonthMarkers(start, end) {
  const out = [];
  const d = new Date(start.getFullYear(), start.getMonth(), 1);
  while (d <= end) {
    out.push({ ms: d.getTime(), label: d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }) });
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════
// Timeline view — chronological milestones grouped by quarter
// ═══════════════════════════════════════════════════════════════
function TimelineView({ projects }) {
  const rows = projects
    .filter((p) => p.planned_start_date || p.actual_start_date || p.start_date)
    .map((p) => ({ ...p, sortDate: p.planned_start_date || p.actual_start_date || p.start_date }))
    .sort((a, b) => (a.sortDate || "").localeCompare(b.sortDate || ""));
  const groups = new Map();
  rows.forEach((p) => {
    const q = quarterOf(p.sortDate);
    if (!groups.has(q)) groups.set(q, []);
    groups.get(q).push(p);
  });
  return (
    <div className="space-y-6" data-testid="projects-timeline-view">
      {Array.from(groups.entries()).map(([q, ps]) => (
        <div key={q}>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border pb-2 mb-3">{q} · {ps.length}</div>
          <div className="space-y-2">
            {ps.map((p) => (
              <Link key={p.id} to={`/app/projects/${p.id}`} className="flex items-start gap-3 border-l-2 pl-3 py-2 hover:bg-muted/30 group" style={{ borderColor: healthBorderColor(p.health) }} data-testid={`timeline-row-${p.id}`}>
                <div className="w-24 text-[10px] text-muted-foreground font-mono pt-0.5">{(p.sortDate || "").slice(0, 10)}</div>
                <HealthCell health={p.health} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate group-hover:text-primary">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {p.client?.name || p.client_name || "—"}
                    {p.brand?.name && <span> · {p.brand.name}</span>}
                    {p.planned_end_date && <span> · ends {p.planned_end_date}</span>}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function quarterOf(iso) {
  if (!iso) return "Undated";
  const d = new Date(iso);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}
function healthBorderColor(h) {
  return { on_track: "#10b981", at_risk: "#f59e0b", off_track: "#f43f5e", blocked: "#b91c1c" }[h] || "#94a3b8";
}
// R73c · Very-light tint for card background based on risk color.
function healthTintColor(h) {
  return { on_track: "#ecfdf5", at_risk: "#fffbeb", off_track: "#fff1f2", blocked: "#fef2f2" }[h] || "#ffffff";
}

// ═══════════════════════════════════════════════════════════════
// R68 · Feed view — per-project cards with sort + quick filters +
// per-card "Post update" button. Mobile-first grid: 1 col on phones,
// 2 on md+, 3 on lg+. Each card shows the latest update or latest
// comment inline; tapping "+ Post update" opens the composer in a
// small inline sheet without leaving the feed.
// R71 · Director-facing upgrades:
//   • Sort with asc/desc toggle on every parameter (name, activity, status
//     update, created, project owner, last updated by).
//   • People filters — filter cards by Project Owner and Last Updated By.
//   • Card action row now has icon buttons for Task Peek, Updates History
//     Peek, Quick Add Task — Post Update button is compact (icon + label)
//     so all four fit on mobile.
//   • Task-count-by-status badges below each card (open / in_progress /
//     blocked / done). Overdue-if-any highlights in red on hover.
// ═══════════════════════════════════════════════════════════════
function FeedView({ projects, wsId, members = [], onRefresh }) {
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortDir, setSortDir] = useState("desc"); // R71 · direction toggle
  const [quickFilter, setQuickFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("");        // R71 · project owner
  const [updaterFilter, setUpdaterFilter] = useState("");    // R71 · last updated by
  const [composerProject, setComposerProject] = useState(null); // {id, name}
  const [peekProject, setPeekProject] = useState(null);      // R71 · TaskPeekDialog target
  const [updatesPeek, setUpdatesPeek] = useState(null);      // R71 · Updates history dialog target
  const [quickAddProject, setQuickAddProject] = useState(null); // R71 · Quick add task target
  const [milestonePeek, setMilestonePeek] = useState(null);  // R72 · Upcoming milestones dialog target
  const [nudgeTarget, setNudgeTarget] = useState(null);      // R72b · nudge dialog target
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [clarityOpen, setClarityOpen] = useState(false);     // R73b · Clarity coaching report dialog
  const [layout, setLayout] = useState("card");              // R73c · card | list (single-column) view toggle
  const [ownerActivityMode, setOwnerActivityMode] = useState("any"); // R73c · any activity | own posts only
  const [q, setQ] = useState("");

  // R71 · Owner / updater option lists derived from visible projects so
  // dropdowns never contain stale ex-members.
  const ownerOptions = useMemo(() => {
    const seen = new Map();
    for (const p of projects) {
      const id = p.created_by || p.owner_id;
      const name = p.created_by_name || p.owner_name;
      if (id && !seen.has(id)) seen.set(id, name || id);
    }
    return Array.from(seen, ([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [projects]);
  const updaterOptions = useMemo(() => {
    const seen = new Map();
    for (const p of projects) {
      const name = p.latest_update?.author_name || p.updated_by_name;
      const id = p.updated_by_id || name; // fall back to name string as key
      if (name && !seen.has(id)) seen.set(id, name);
    }
    return Array.from(seen, ([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [projects]);

  // R71 · Owner activity summary — groups every distinct project owner by
  // whether ANY project they own has a recent status update. R73c · Added
  // `ownerActivityMode`: "any" (any activity on their projects) or
  // "own" (only updates where the OWNER themselves authored the post).
  const ownerActivity = useMemo(() => {
    const byOwner = new Map();
    const now = Date.now();
    for (const p of projects) {
      const id = p.created_by || p.owner_id;
      const name = p.created_by_name || p.owner_name;
      if (!id) continue;
      const cur = byOwner.get(id) || {
        id, name: name || id, project_count: 0,
        latest_update_at: null, latest_update_project: null,
        stale_project_count: 0,
      };
      cur.project_count += 1;
      // In "own" mode we only count updates authored by the owner themselves.
      const upd = p.latest_update || null;
      const owned = ownerActivityMode === "own"
        ? (upd && upd.author_id === id ? upd : null)
        : upd;
      const at = owned?.created_at;
      if (at) {
        if (!cur.latest_update_at || at > cur.latest_update_at) {
          cur.latest_update_at = at;
          cur.latest_update_project = p.name;
        }
      }
      const isStale = !at || (now - new Date(at).getTime()) > 7 * 24 * 3600 * 1000;
      if (isStale) cur.stale_project_count += 1;
      byOwner.set(id, cur);
    }
    const rows = Array.from(byOwner.values());
    for (const r of rows) {
      if (!r.latest_update_at) {
        r.bucket = "silent";
        r.age_days = null;
      } else {
        const ageMs = now - new Date(r.latest_update_at).getTime();
        r.age_days = Math.floor(ageMs / (24 * 3600 * 1000));
        if (ageMs <= 7 * 24 * 3600 * 1000) r.bucket = "active";
        else if (ageMs <= 30 * 24 * 3600 * 1000) r.bucket = "quiet";
        else r.bucket = "silent";
      }
    }
    const active = rows.filter((r) => r.bucket === "active")
      .sort((a, b) => (b.latest_update_at || "").localeCompare(a.latest_update_at || ""));
    const quiet = rows.filter((r) => r.bucket === "quiet")
      .sort((a, b) => (a.age_days || 0) - (b.age_days || 0));
    const silent = rows.filter((r) => r.bucket === "silent")
      .sort((a, b) => b.stale_project_count - a.stale_project_count);
    return { total: rows.length, active, quiet, silent };
  }, [projects, ownerActivityMode]);

  const rows = useMemo(() => {
    const now = new Date();
    const isStale = (p) => {
      const at = p.latest_update?.created_at;
      if (!at) return true;
      return (now - new Date(at)) > 7 * 24 * 3600 * 1000;
    };
    let list = projects.map((p) => {
      const lastActivity = p.latest_update?.created_at || p.latest_comment?.created_at || p.updated_at || p.created_at || "";
      return { ...p, _last_activity: lastActivity };
    });
    // Quick filters
    if (quickFilter === "at_risk") list = list.filter((p) => (p.health || "") === "at_risk" || (p.health || "") === "off_track");
    else if (quickFilter === "no_updates") list = list.filter((p) => !p.latest_update);
    else if (quickFilter === "stale") list = list.filter(isStale);
    else if (quickFilter === "paused") list = list.filter((p) => ["hold", "paused", "awaiting_work", "inactive"].includes((p.status || "").toLowerCase()));
    else if (quickFilter === "active") list = list.filter((p) => !["done", "archived"].includes((p.status || "").toLowerCase()));
    // R71 · People filters
    if (ownerFilter) list = list.filter((p) => (p.created_by || p.owner_id) === ownerFilter);
    if (updaterFilter) {
      list = list.filter((p) => {
        const key = p.updated_by_id || p.latest_update?.author_name || p.updated_by_name || "";
        return key === updaterFilter;
      });
    }
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(qq) || (p.client_name || "").toLowerCase().includes(qq));
    }
    // Sort (each cmp returns a "descending" ordering; we flip via sortDir)
    const cmp = {
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
      last_activity: (a, b) => (b._last_activity || "").localeCompare(a._last_activity || ""),
      last_status_update: (a, b) => (b.latest_update?.created_at || "").localeCompare(a.latest_update?.created_at || ""),
      created: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      project_owner: (a, b) => (a.created_by_name || "").localeCompare(b.created_by_name || ""),
      last_updated_by: (a, b) => (a.latest_update?.author_name || a.updated_by_name || "").localeCompare(b.latest_update?.author_name || b.updated_by_name || ""),
    }[sortBy] || (() => 0);
    list.sort(sortDir === "asc" ? (a, b) => -cmp(a, b) : cmp);
    return list;
  }, [projects, sortBy, sortDir, quickFilter, ownerFilter, updaterFilter, q]);

  const filterChips = [
    { v: "all",         label: `All (${projects.length})` },
    { v: "active",      label: "Active" },
    { v: "at_risk",     label: "At risk / Off-track" },
    { v: "no_updates",  label: "No updates" },
    { v: "stale",       label: "Stale (>7d)" },
    { v: "paused",      label: "Paused" },
  ];

  return (
    <div
      className="space-y-3 min-w-0 overflow-x-hidden feed-strong-text"
      data-testid="projects-feed-view"
    >
      {/* R71b · Owner activity summary — Active / Quiet / Silent buckets.
          Click any owner chip to filter the feed. Collapsible so it doesn't
          steal too much space on mobile. */}
      <OwnerActivitySummary
        activity={ownerActivity}
        onPickOwner={(id) => setOwnerFilter(id)}
        activeOwnerFilter={ownerFilter}
        onNudgeOwner={(o) => setNudgeTarget(o)}
        onOpenClarityReport={() => setClarityOpen(true)}
        mode={ownerActivityMode}
        onChangeMode={setOwnerActivityMode}
      />
      {/* Toolbar — sort + quick filters + search. Wraps naturally on
          phones so nothing runs off-screen. */}
      <div className="flex flex-wrap items-center gap-2 border border-border bg-card p-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search project or client…"
          className="h-8 text-xs rounded-none w-full sm:w-56"
          data-testid="projects-feed-search"
        />
        <div className="flex items-center gap-1">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-full sm:w-52 rounded-none text-xs" data-testid="projects-feed-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_activity">Sort · Last activity</SelectItem>
              <SelectItem value="last_status_update">Sort · Last status update</SelectItem>
              <SelectItem value="name">Sort · Project name</SelectItem>
              <SelectItem value="created">Sort · Created date</SelectItem>
              <SelectItem value="project_owner">Sort · Project owner</SelectItem>
              <SelectItem value="last_updated_by">Sort · Last updated by</SelectItem>
            </SelectContent>
          </Select>
          {/* R71 · Direction toggle — sits right after the sort dropdown */}
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="h-8 w-8 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center"
            title={sortDir === "asc" ? "Sort ascending — click for descending" : "Sort descending — click for ascending"}
            data-testid="projects-feed-sort-dir"
          >
            {sortDir === "asc"
              ? <TrendingUp className="w-3.5 h-3.5 text-primary" />
              : <TrendingDown className="w-3.5 h-3.5 text-primary" />}
          </button>
          {/* R73c · Layout toggle — Card grid ↔ single-column List */}
          <button
            type="button"
            onClick={() => setLayout((l) => (l === "card" ? "list" : "card"))}
            className="h-8 w-8 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center"
            title={layout === "card" ? "Switch to List view" : "Switch to Card view"}
            data-testid="projects-feed-layout-toggle"
          >
            {layout === "card"
              ? <List className="w-3.5 h-3.5 text-primary" />
              : <LayoutGrid className="w-3.5 h-3.5 text-primary" />}
          </button>
          {/* R73d · Refresh — re-runs the projects fetch WITHOUT changing
              any sort or filter state so the view rerenders in place. */}
          {onRefresh && (
            <button
              type="button"
              onClick={() => onRefresh()}
              className="h-8 w-8 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center"
              title="Refresh — keeps current sort & filters"
              data-testid="projects-feed-refresh"
            >
              <RotateCw className="w-3.5 h-3.5 text-primary" />
            </button>
          )}
        </div>
        {/* R71 · People filters */}
        <SearchableSelect
          value={ownerFilter}
          onValueChange={setOwnerFilter}
          options={[{ value: "", label: "All owners" }, ...ownerOptions]}
          placeholder="Owner…"
          clearable
          triggerClassName="h-8 rounded-none text-xs w-full sm:w-40"
          data-testid="projects-feed-filter-owner"
          width={160}
        />
        <SearchableSelect
          value={updaterFilter}
          onValueChange={setUpdaterFilter}
          options={[{ value: "", label: "All updaters" }, ...updaterOptions]}
          placeholder="Last updated by…"
          clearable
          triggerClassName="h-8 rounded-none text-xs w-full sm:w-40"
          data-testid="projects-feed-filter-updater"
          width={160}
        />
        <div className="flex items-center gap-1 flex-wrap w-full sm:w-auto sm:ml-auto">
          {filterChips.map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setQuickFilter(c.v)}
              className={`h-8 text-[10px] uppercase tracking-widest px-2 border ${quickFilter === c.v ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted/40 border-border"}`}
              data-testid={`projects-feed-filter-${c.v}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No projects match this filter" description="Adjust filters or add a status update to see activity." icon={Rss} testid="projects-feed-empty" />
      ) : (
        <div
          className={
            layout === "list"
              ? "flex flex-col gap-3"
              : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
          }
          data-testid="projects-feed-grid"
          data-layout={layout}
        >
          {rows.map((p) => {
            const u = p.latest_update;
            const c = p.latest_comment;
            const isStale = !u || (Date.now() - new Date(u.created_at).getTime()) > 7 * 24 * 3600 * 1000;
            const hColor = healthBorderColor(p.health);
            const hTint = healthTintColor(p.health);
            return (
              <div
                key={p.id}
                className="p-3 flex flex-col gap-2 hover:shadow-sm transition-shadow border-l-4 border-t border-r border-b border-border/60"
                style={{ borderLeftColor: hColor, backgroundColor: hTint }}
                data-testid={`feed-card-${p.id}`}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Link to={`/app/projects/${p.id}`} className="font-semibold text-sm truncate flex-1 hover:text-primary">
                    {p.name}
                  </Link>
                  {/* R73 · Overdue / upcoming milestone chip — one-glance
                      deadline flag for directors. */}
                  <MilestoneFlagChip
                    flag={p.milestone_flag}
                    onClick={() => setMilestonePeek({ id: p.id, name: p.name })}
                    testid={`feed-card-milestone-flag-${p.id}`}
                  />
                  {/* R73c · Health chip retained for a11y/label — border-l
                      colour already conveys risk visually. */}
                  <span
                    className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border shrink-0 font-semibold"
                    style={{ borderColor: hColor, color: hColor, backgroundColor: "white" }}
                  >{HEALTH_LABEL[p.health || "on_track"]}</span>
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {p.client_name || "—"}{p.brand_name ? ` · ${p.brand_name}` : ""}
                </div>
                {/* R71 · People strip — Owner & last-updater */}
                <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
                  {p.created_by_name && (
                    <span className="inline-flex items-center gap-1" data-testid={`feed-card-owner-${p.id}`}>
                      <span className="uppercase tracking-widest text-[9px] font-semibold">Owner</span>
                      <span className="text-foreground">{p.created_by_name}</span>
                    </span>
                  )}
                  {(u?.author_name || p.updated_by_name) && (
                    <span className="inline-flex items-center gap-1" data-testid={`feed-card-updater-${p.id}`}>
                      <span className="uppercase tracking-widest text-[9px] font-semibold">Updated by</span>
                      <span className="text-foreground">{u?.author_name || p.updated_by_name}</span>
                    </span>
                  )}
                </div>
                {u ? (
                  <div className={`border-l-2 pl-2 py-1 text-xs ${isStale ? "border-amber-400" : "border-primary/60"} bg-white/60`}>
                    <div className="text-[10px] text-muted-foreground mb-0.5">
                      {u.author_name || "—"} · {fmtRelative(u.created_at)}
                      {isStale && <span className="ml-1 text-amber-700">· stale</span>}
                    </div>
                    {/* R72c · Long-update Read more / Hide toggle. */}
                    <FeedUpdateBody update={u} pid={p.id} />
                    {/* R73c · Inline thumbs-up / thumbs-down reactions on the card */}
                    <StatusUpdateReactions
                      projectId={p.id}
                      update={u}
                      onChange={onRefresh}
                    />
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic border-l-2 border-dashed border-border pl-2 py-1 bg-white/60">
                    No status updates yet
                  </div>
                )}
                {c && (!u || c.created_at > u.created_at) && (
                  <div className="border-l-2 border-sky-400 pl-2 py-1 text-xs">
                    <div className="text-[10px] text-muted-foreground mb-0.5">Latest comment · {fmtRelative(c.created_at)}</div>
                    <div className="text-xs whitespace-pre-wrap line-clamp-3">{c.body_preview || c.body_full}</div>
                  </div>
                )}
                {/* R71 · Action row — compact icons so all four buttons fit on a phone.
                    R73d · Update button reduced to natural width (not
                    flex-1) so it no longer dominates the row. */}
                <div className="flex items-center gap-1 mt-auto pt-1 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => setComposerProject({ id: p.id, name: p.name })}
                    className="rounded-none h-8 text-xs px-3"
                    data-testid={`feed-post-update-${p.id}`}
                    title="Post update"
                  >
                    <Plus className="w-3 h-3 mr-1 shrink-0" />
                    <span>Update</span>
                  </Button>
                  <button
                    type="button"
                    onClick={() => setPeekProject({ id: p.id, name: p.name })}
                    className="h-8 w-9 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center shrink-0"
                    title="Peek open tasks"
                    data-testid={`feed-peek-tasks-${p.id}`}
                  >
                    <ListTodo className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setUpdatesPeek({ id: p.id, name: p.name })}
                    className="h-8 w-9 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center shrink-0"
                    title="Updates history"
                    data-testid={`feed-peek-updates-${p.id}`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickAddProject({ id: p.id, name: p.name })}
                    className="h-8 w-9 border border-border bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center justify-center shrink-0"
                    title="Quick add task"
                    data-testid={`feed-quick-add-task-${p.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  {/* R72 · Milestone peek — 4th icon for directors */}
                  <button
                    type="button"
                    onClick={() => setMilestonePeek({ id: p.id, name: p.name })}
                    className="h-8 w-9 border border-border bg-card hover:bg-muted/40 inline-flex items-center justify-center shrink-0"
                    title="Upcoming milestones"
                    data-testid={`feed-peek-milestones-${p.id}`}
                  >
                    <Flag className="w-3.5 h-3.5" />
                  </button>
                  <Link
                    to={`/app/projects/${p.id}?tab=updates`}
                    className="h-8 w-9 border border-border text-xs inline-flex items-center justify-center hover:bg-muted shrink-0"
                    title="Open project page"
                    data-testid={`feed-open-updates-${p.id}`}
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                {/* R71 · Task-count-by-status badges — director sees delivery
                    load at a glance without opening the project. */}
                <TaskCountsRow counts={p.task_counts || {}} testid={`feed-card-counts-${p.id}`} />
                {/* R72 · Billing lifecycle chips — revenue-blocking work
                    is now visible without opening the project. */}
                <BillingCountsRow counts={p.billing_counts || {}} testid={`feed-card-billing-${p.id}`} />
              </div>
            );
          })}
        </div>
      )}

      {composerProject && (
        <Dialog open onOpenChange={(o) => {
          if (!o) {
            setComposerProject(null);
            // R73c · After the composer closes, refetch projects so the
            // Feed re-sorts based on the newly-posted update timestamp
            // WITHOUT a full page reload.
            if (onRefresh) onRefresh();
          }
        }}>
          <DialogContent className="w-[95vw] max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="feed-composer-dialog">
            <DialogHeader className="flex flex-row items-center justify-between gap-2 pr-6">
              <DialogTitle className="text-sm">Post update · {composerProject.name}</DialogTitle>
              {/* R73d · Quick task button also available from Post Update popup */}
              <Button
                size="sm"
                variant="outline"
                className="rounded-none h-7 text-xs"
                onClick={() => { setQuickAddProject({ id: composerProject.id, name: composerProject.name }); }}
                data-testid="feed-composer-quick-add-task"
                title="Also add a task on this project"
              >
                <Plus className="w-3 h-3 mr-1" /> Add task
              </Button>
            </DialogHeader>
            <ProjectStatusUpdatesTab
              projectId={composerProject.id}
              project={{ id: composerProject.id, name: composerProject.name }}
              projectMembers={[]}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* R71 · Task peek dialog — reused shared component */}
      {peekProject && (
        <TaskPeekDialog
          open
          onOpenChange={(o) => { if (!o) setPeekProject(null); }}
          wsId={wsId}
          projectId={peekProject.id}
          projectName={peekProject.name}
          onAddTask={() => { setQuickAddProject(peekProject); setPeekProject(null); }}
        />
      )}

      {/* R71 · Updates history peek dialog */}
      {updatesPeek && (
        <Dialog open onOpenChange={(o) => { if (!o) setUpdatesPeek(null); }}>
          <DialogContent className="w-[95vw] max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="feed-updates-history-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">Updates history · {updatesPeek.name}</DialogTitle>
            </DialogHeader>
            <ProjectUpdatesHistory projectId={updatesPeek.id} />
          </DialogContent>
        </Dialog>
      )}

      {/* R71 · Quick add task popover — auto-opens & pre-fills project.
          R73d · No `trigger` prop → uses the centered Dialog fallback so
          the popup no longer floats at the top-left of the viewport. */}
      {quickAddProject && (
        <QuickAddTaskPopover
          wsId={wsId}
          preset={{ project_id: quickAddProject.id }}
          projects={projects.map((p) => ({ id: p.id, name: p.name }))}
          members={members}
          defaultOpen
          onOpenChange={(o) => { if (!o) setQuickAddProject(null); }}
          onCreated={() => { setQuickAddProject(null); toast.success("Task added"); if (onRefresh) onRefresh(); }}
        />
      )}
      {/* R72 · Upcoming milestones peek dialog */}
      {milestonePeek && (
        <Dialog open onOpenChange={(o) => { if (!o) setMilestonePeek(null); }}>
          <DialogContent className="w-[95vw] max-w-xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="feed-milestones-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm inline-flex items-center gap-2">
                <Flag className="w-3.5 h-3.5 text-primary" />
                Upcoming milestones · {milestonePeek.name}
              </DialogTitle>
            </DialogHeader>
            <ProjectMilestonesPeek projectId={milestonePeek.id} />
          </DialogContent>
        </Dialog>
      )}

      {/* R72b · Owner nudge confirmation dialog. Sends in-app + email
          reminder and deducts points via /workspaces/{ws_id}/owner-nudge. */}
      {nudgeTarget && (
        <Dialog open onOpenChange={(o) => { if (!o) { setNudgeTarget(null); setNudgeBusy(false); } }}>
          <DialogContent className="w-[95vw] max-w-md rounded-none" data-testid="feed-owner-nudge-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm inline-flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-rose-600" />
                Nudge {nudgeTarget.name}?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>
                We&apos;ll send an <span className="font-semibold">in-app notification and an email</span> asking
                {" "}{nudgeTarget.name} to post a fresh status update on their{" "}
                <span className="font-semibold">{nudgeTarget.project_count}</span> project
                {nudgeTarget.project_count === 1 ? "" : "s"}.
              </p>
              <p className="text-xs text-rose-700 border-l-2 border-rose-400 pl-2">
                Points will be deducted from {nudgeTarget.name}&apos;s score for having to be reminded.
                Adjust the deduction from Settings → Points → &quot;Owner nudged for missing update&quot;.
              </p>
              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  disabled={nudgeBusy}
                  onClick={() => { setNudgeTarget(null); setNudgeBusy(false); }}
                  data-testid="feed-owner-nudge-cancel"
                >Cancel</Button>
                <Button
                  size="sm"
                  className="rounded-none"
                  disabled={nudgeBusy}
                  onClick={async () => {
                    setNudgeBusy(true);
                    try {
                      const stale_ids = projects
                        .filter((p) => (p.created_by || p.owner_id) === nudgeTarget.id)
                        .filter((p) => !p.latest_update
                          || (Date.now() - new Date(p.latest_update.created_at).getTime()) > 7 * 24 * 3600 * 1000)
                        .slice(0, 20)
                        .map((p) => p.id);
                      const r = await api.post(`/workspaces/${wsId}/owner-nudge`, {
                        user_id: nudgeTarget.id,
                        project_ids: stale_ids,
                        channels: ["in_app", "email"],
                        deduct_points: true,
                      });
                      const parts = [];
                      if (r.data?.in_app_sent) parts.push("in-app sent");
                      if (r.data?.email_status === "sent") parts.push("email sent");
                      else if (r.data?.email_status === "skipped") parts.push("email skipped");
                      else if (r.data?.email_status === "failed") parts.push("email failed");
                      const pts = r.data?.points_deducted || 0;
                      if (pts) parts.push(`${pts > 0 ? "+" : ""}${pts} points`);
                      toast.success(`Nudged ${nudgeTarget.name} — ${parts.join(" · ")}`);
                      setNudgeTarget(null);
                    } catch (e) {
                      toast.error(apiErrorMessage(e, "Couldn't send nudge"));
                    } finally {
                      setNudgeBusy(false);
                    }
                  }}
                  data-testid="feed-owner-nudge-confirm"
                >
                  {nudgeBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                  Send nudge
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* R73b · Clarity coaching report dialog */}
      {clarityOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setClarityOpen(false); }}>
          <DialogContent className="w-[95vw] max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="feed-clarity-report-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm inline-flex items-center gap-2">
                <ThumbsDown className="w-3.5 h-3.5 text-rose-600" />
                Clarity Coaching · Status Update Reactions
              </DialogTitle>
            </DialogHeader>
            <ClarityCoachingReport wsId={wsId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
// R71b · Owner activity summary — top-of-feed panel that answers the
// director's core question at a glance: "Which of my project owners are
// actively posting updates and which have gone quiet?"
// Buckets:
//   • active — posted an update on any project they own in the last 7 days
//   • quiet  — last update 8-30 days ago
//   • silent — no updates ever OR > 30 days ago
// Each owner chip is clickable → sets the Owner filter on the feed.
function OwnerActivitySummary({ activity, onPickOwner, activeOwnerFilter, onNudgeOwner, onOpenClarityReport, mode = "any", onChangeMode }) {
  const [expanded, setExpanded] = useState(true);
  if (!activity || activity.total === 0) return null;
  const cols = [
    {
      key: "active",
      label: "Active",
      caption: "Posted in the last 7 days",
      list: activity.active,
      chipCls: "border-emerald-500 text-emerald-800 bg-emerald-50 hover:bg-emerald-100",
      dot: "bg-emerald-500",
      empty: "No one has posted an update in the last week.",
    },
    {
      key: "quiet",
      label: "Quiet",
      caption: "Last update 8-30 days ago",
      list: activity.quiet,
      chipCls: "border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100",
      dot: "bg-amber-500",
      empty: "Nobody sitting in the 8-30 day gap.",
    },
    {
      key: "silent",
      label: "Silent",
      caption: "No updates ever or > 30d",
      list: activity.silent,
      chipCls: "border-rose-500 text-rose-800 bg-rose-50 hover:bg-rose-100",
      dot: "bg-rose-500",
      empty: "Every owner has posted recently — nice.",
    },
  ];
  return (
    <div className="border border-border bg-card" data-testid="projects-feed-owner-activity">
      <div className="w-full flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left hover:opacity-80 flex-1 min-w-0"
          data-testid="projects-feed-owner-activity-toggle"
        >
          <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-xs uppercase tracking-widest font-semibold">Owner activity</span>
          <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal truncate">
            {activity.total} owner{activity.total === 1 ? "" : "s"} ·
            <span className="text-emerald-700 ml-1">{activity.active.length} active</span> ·
            <span className="text-amber-700 ml-1">{activity.quiet.length} quiet</span> ·
            <span className="text-rose-700 ml-1">{activity.silent.length} silent</span>
          </span>
        </button>
        {onChangeMode && (
          <button
            type="button"
            onClick={() => onChangeMode(mode === "own" ? "any" : "own")}
            className={`text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1 border px-2 py-1 ${mode === "own" ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-muted/40"}`}
            title={mode === "own"
              ? "Currently counting ONLY updates the owner themselves posted. Click to include any activity."
              : "Currently counting ANY update on the owner's projects. Click to count only what the owner posted."}
            data-testid="projects-feed-owner-activity-mode"
          >
            {mode === "own" ? "Own posts" : "Any activity"}
          </button>
        )}
        {onOpenClarityReport && (
          <button
            type="button"
            onClick={onOpenClarityReport}
            className="text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1 border border-border px-2 py-1 hover:bg-muted/40"
            title="Weekly clarity coaching report — spot who consistently posts unclear updates"
            data-testid="projects-feed-clarity-coaching-open"
          >
            <ThumbsDown className="w-3 h-3 text-rose-600" />
            Clarity report
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[10px] text-muted-foreground hover:opacity-80"
          data-testid="projects-feed-owner-activity-collapse"
        >
          {expanded ? "Hide" : "Show"}
        </button>
      </div>
      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-border">
          {cols.map((col) => (
            <div
              key={col.key}
              className="p-3 md:border-r md:last:border-r-0 border-border"
              data-testid={`projects-feed-owner-activity-col-${col.key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-block w-2 h-2 rounded-full ${col.dot}`} />
                <span className="text-xs font-semibold uppercase tracking-widest">{col.label}</span>
                <span className="text-[10px] text-muted-foreground">· {col.list.length}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mb-2">{col.caption}</div>
              {col.list.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic">{col.empty}</div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {col.list.slice(0, 12).map((o) => {
                    const isActive = activeOwnerFilter === o.id;
                    const tail =
                      col.key === "active"
                        ? `${o.age_days === 0 ? "today" : `${o.age_days}d`}`
                        : col.key === "quiet"
                          ? `${o.age_days}d ago`
                          : (o.latest_update_at ? `${o.age_days}d ago` : "no updates");
                    const canNudge = onNudgeOwner && (col.key === "silent" || col.key === "quiet");
                    return (
                      <div key={o.id} className="inline-flex items-stretch">
                        <button
                          type="button"
                          onClick={() => onPickOwner(isActive ? "" : o.id)}
                          className={`text-[10px] px-2 py-1 border ${col.chipCls} ${isActive ? "ring-2 ring-primary/50" : ""} ${canNudge ? "border-r-0" : ""}`}
                          title={
                            col.key === "silent" && o.stale_project_count
                              ? `${o.name} — ${o.stale_project_count} stale project${o.stale_project_count === 1 ? "" : "s"} · ${o.project_count} total`
                              : `${o.name} — ${o.project_count} project${o.project_count === 1 ? "" : "s"} · ${tail}`
                          }
                          data-testid={`projects-feed-owner-activity-chip-${col.key}-${o.id}`}
                        >
                          <span className="font-medium">{o.name}</span>
                          <span className="opacity-70 ml-1">· {o.project_count}</span>
                          <span className="opacity-70 ml-1 text-[9px] uppercase">· {tail}</span>
                        </button>
                        {canNudge && (
                          <button
                            type="button"
                            onClick={() => onNudgeOwner(o)}
                            className={`text-[10px] px-1.5 py-1 border ${col.chipCls}`}
                            title={`Nudge ${o.name} to post an update (deducts points)`}
                            data-testid={`projects-feed-owner-nudge-${col.key}-${o.id}`}
                          >
                            <Bell className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {col.list.length > 12 && (
                    <span className="text-[10px] text-muted-foreground italic self-center">+ {col.list.length - 12} more</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TaskCountsRow({ counts, testid }) {
  const items = [
    { key: "open",         label: "Open",     count: counts.open || 0,         cls: "border-neutral-400 text-neutral-700" },
    { key: "in_progress",  label: "In-prog",  count: counts.in_progress || 0,  cls: "border-sky-400 text-sky-700" },
    { key: "blocked",      label: "Blocked",  count: counts.blocked || 0,      cls: "border-rose-400 text-rose-700" },
    { key: "done",         label: "Done",     count: counts.done || 0,         cls: "border-emerald-400 text-emerald-700" },
  ];
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) {
    return (
      <div className="text-[10px] text-muted-foreground italic" data-testid={testid}>
        No tasks yet
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      {items.filter((i) => i.count > 0).map((i) => (
        <span
          key={i.key}
          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border bg-card ${i.cls}`}
          data-testid={`${testid}-${i.key}`}
        >
          {i.label} · {i.count}
        </span>
      ))}
    </div>
  );
}

// R73b · Clarity Coaching Report — pulls the workspace-wide reaction stats
// from GET /workspaces/{ws}/reactions/clarity-report and lists authors
// sorted by highest down-rate. Managers use this to spot who consistently
// posts unclear updates and click through to a sample update for context.
function ClarityCoachingReport({ wsId }) {
  const [days, setDays] = useState(7);
  const [minUpdates, setMinUpdates] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/workspaces/${wsId}/reactions/clarity-report`, {
          params: { days, min_updates: minUpdates },
        });
        if (!ignore) setData(r.data);
      } catch (e) { if (!ignore) { toast.error(apiErrorMessage(e, "Report failed")); setData(null); } }
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [wsId, days, minUpdates]);
  const rows = data?.rows || [];
  const totals = data?.totals || { updates: 0, up: 0, down: 0, authors: 0 };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">Range:</span>
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`h-7 px-2 border ${days === d ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted/40"} text-[10px] uppercase tracking-widest`}
            data-testid={`clarity-range-${d}`}
          >Last {d}d</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[10px] text-muted-foreground">Min updates</label>
          <select
            value={minUpdates}
            onChange={(e) => setMinUpdates(parseInt(e.target.value, 10) || 1)}
            className="h-7 border border-border bg-card text-xs px-1 rounded-none"
            data-testid="clarity-min-updates"
          >
            {[1, 2, 3, 5].map((n) => <option key={n} value={n}>≥ {n}</option>)}
          </select>
        </div>
      </div>
      {/* Totals strip */}
      <div className="border border-border bg-muted/20 p-2 text-[11px] flex flex-wrap items-center gap-3">
        <span><strong>{totals.updates}</strong> updates</span>
        <span className="text-emerald-700"><strong>{totals.up}</strong> up</span>
        <span className="text-rose-700"><strong>{totals.down}</strong> down</span>
        <span className="text-muted-foreground">across {totals.authors} author{totals.authors === 1 ? "" : "s"}</span>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-4 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Building report…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 italic text-center">
          No status updates in this range yet — check back after your team posts a few.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="clarity-rows">
          {rows.map((r) => {
            const pct = Math.round((r.down_rate || 0) * 100);
            const tone =
              pct >= 40 ? "border-rose-500 bg-rose-50/40"
              : pct >= 20 ? "border-amber-500 bg-amber-50/40"
              : r.up > 0 ? "border-emerald-500 bg-emerald-50/40"
              : "border-border";
            return (
              <li
                key={r.author_id}
                className={`border-l-4 pl-3 py-2 ${tone}`}
                data-testid={`clarity-row-${r.author_id}`}
              >
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                  <span className="font-semibold text-sm">{r.author_name}</span>
                  <span className="text-[10px] text-muted-foreground">
                    · {r.updates} update{r.updates === 1 ? "" : "s"}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px]">
                    <span className="inline-flex items-center gap-0.5 text-emerald-700"><ThumbsUp className="w-3 h-3" /> {r.up}</span>
                    <span className="inline-flex items-center gap-0.5 text-rose-700"><ThumbsDown className="w-3 h-3" /> {r.down}</span>
                    <span className={`ml-1 uppercase tracking-widest text-[9px] px-1.5 py-0.5 border ${pct >= 40 ? "border-rose-500 text-rose-700 bg-rose-50" : pct >= 20 ? "border-amber-500 text-amber-800 bg-amber-50" : "border-neutral-400 text-neutral-700 bg-neutral-50"}`}>
                      {pct}% ✗
                    </span>
                  </span>
                </div>
                {r.sample_low && (
                  <div className="mt-1.5 text-[11px] text-muted-foreground">
                    <span className="uppercase tracking-widest text-[9px] font-semibold mr-1">Lowest-rated sample</span>
                    · {r.sample_low.project_name}
                    <div className="mt-0.5 text-xs italic text-foreground line-clamp-3">&ldquo;{r.sample_low.body_preview}&rdquo;</div>
                  </div>
                )}
                {r.up === 0 && r.down === 0 && r.no_reaction > 0 && (
                  <div className="text-[10px] text-muted-foreground italic mt-1">
                    No reactions yet on any of their updates.
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



// R72 · Billing lifecycle chips row for a Feed card. Buckets:
//   • non_billable
//   • billable_unbilled            — work done, not yet invoiced
//   • billable_billed_unpaid       — invoiced, awaiting payment (money owed)
//   • billable_billed_paid         — revenue landed

// R72c · Long-update Read more / Hide toggle for the Feed card body.
// Renders SmartRichText inside a collapsible container. We measure the
// natural scroll height on mount and only expose the toggle when the
// content actually overflows the collapsed 6-line clamp — no chevron
// appears on 1-2 line updates.
function FeedUpdateBody({ update, pid }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);
  // R72c-fix · SmartRichTextDisplay hydrates innerHTML in a later paint
  // tick, so a single setTimeout(60ms) measurement misses the growth.
  // We attach a ResizeObserver so overflow flips on as soon as the rich
  // text expands the wrap element past the 6-line clamp.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let mounted = true;
    const measure = () => {
      if (!mounted || !ref.current) return;
      const cur = ref.current;
      setOverflows(cur.scrollHeight > cur.clientHeight + 4);
    };
    measure();
    let ro;
    try {
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(measure);
        ro.observe(el);
      }
    } catch { /* older browsers — measurement fallback below */ }
    // Belt-and-braces: a few retry ticks in case rich-text hydration
    // triggers no size change (e.g. images loading late).
    const timers = [setTimeout(measure, 80), setTimeout(measure, 300), setTimeout(measure, 900)];
    return () => {
      mounted = false;
      try { ro && ro.disconnect(); } catch { /* ignore */ }
      timers.forEach(clearTimeout);
    };
  }, [update?.body_html, update?.body_full, update?.body_preview]);
  return (
    <div>
      <div
        ref={ref}
        className={`text-sm [&_p]:m-0 [&_ul]:my-1 [&_ol]:my-1 ${expanded ? "" : "line-clamp-6"}`}
        data-testid={`feed-card-body-wrap-${pid}`}
      >
        {update.body_html
          ? <SmartRichTextDisplay html={update.body_html} testid={`feed-card-body-${pid}`} />
          : <span className="whitespace-pre-wrap">{update.body_full || update.body_preview || ""}</span>}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline"
          data-testid={`feed-card-body-toggle-${pid}`}
        >
          {expanded ? "Hide" : "Read more"}
        </button>
      )}
    </div>
  );
}


// Directors care most about the middle two — they're revenue-blocking.
// R73 · Removed the leading $ icon (user feedback). Added a small
// "Billable · N" total chip up front so it's unambiguous which items are
// counted as billable vs the standalone Non-bill number.
function BillingCountsRow({ counts, testid }) {
  const billable_total =
    (counts.billable_unbilled || 0)
    + (counts.billable_billed_unpaid || 0)
    + (counts.billable_billed_paid || 0);
  const items = [
    { key: "billable_unbilled",        label: "Unbilled",  count: counts.billable_unbilled || 0,        cls: "border-amber-500 text-amber-800 bg-amber-50" },
    { key: "billable_billed_unpaid",   label: "Unpaid",    count: counts.billable_billed_unpaid || 0,   cls: "border-rose-500 text-rose-800 bg-rose-50" },
    { key: "billable_billed_paid",     label: "Paid",      count: counts.billable_billed_paid || 0,     cls: "border-emerald-500 text-emerald-800 bg-emerald-50" },
    { key: "non_billable",             label: "Non-bill",  count: counts.non_billable || 0,             cls: "border-neutral-400 text-neutral-700 bg-neutral-50" },
  ];
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      {billable_total > 0 && (
        <span
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-primary/60 text-primary bg-primary/5 font-semibold"
          title={`Total billable tasks (unbilled + unpaid + paid): ${billable_total}`}
          data-testid={`${testid}-billable-total`}
        >
          Billable · {billable_total}
        </span>
      )}
      {items.filter((i) => i.count > 0).map((i) => (
        <span
          key={i.key}
          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${i.cls}`}
          data-testid={`${testid}-${i.key}`}
          title={`${i.label} tasks: ${i.count}`}
        >
          {i.label} · {i.count}
        </span>
      ))}
    </div>
  );
}

// R72 · Upcoming milestones peek — reused from routes_projects_extra
// GET /projects/{pid}/milestones. We show open milestones due within 60d
// (or all if the project has none due). Overdue ones bubble to the top
// with a red badge.
// R73 · Milestone flag chip for the Feed card header. Shows a red flag
// with count if any milestone is overdue, else amber if any is upcoming
// within 7 days. Silent when neither applies. Clickable to open the
// milestone peek dialog for that project.
function MilestoneFlagChip({ flag, onClick, testid }) {
  const overdue = (flag || {}).overdue || 0;
  const upcoming = (flag || {}).upcoming_7d || 0;
  if (!overdue && !upcoming) return null;
  const isOver = overdue > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border shrink-0 inline-flex items-center gap-1 ${
        isOver
          ? "border-rose-500 text-rose-700 bg-rose-50 hover:bg-rose-100"
          : "border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100"
      }`}
      title={
        isOver
          ? `${overdue} overdue milestone${overdue === 1 ? "" : "s"}${upcoming ? ` · ${upcoming} upcoming` : ""}${flag.next_due ? ` · next: ${flag.next_due}` : ""}`
          : `${upcoming} milestone${upcoming === 1 ? "" : "s"} due in the next 7 days${flag.next_due ? ` · next: ${flag.next_due}` : ""}`
      }
      data-testid={testid}
    >
      <Flag className="w-3 h-3" />
      {isOver ? `${overdue} overdue` : `${upcoming} soon`}
    </button>
  );
}


function ProjectMilestonesPeek({ projectId, canCreate = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/milestones`);
      setRows(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);
  const upcoming = useMemo(() => {
    const closed = new Set(["done", "completed", "cancelled"]);
    const open = rows.filter((r) => !closed.has(String(r.status || "").toLowerCase()));
    open.sort((a, b) => (a.due_date || "9999-99-99").localeCompare(b.due_date || "9999-99-99"));
    return open;
  }, [rows]);
  const submit = async () => {
    const title = newTitle.trim();
    if (!title) { toast.error("Give the milestone a title"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/milestones`, {
        title, due_date: newDate || null, status: "planned",
      });
      setNewTitle(""); setNewDate(""); setCreating(false);
      toast.success("Milestone added");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't create milestone")); }
    finally { setBusy(false); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-3">
      {/* R73 · + Add milestone quick form */}
      {canCreate && (
        <div className="border border-dashed border-border p-2 flex flex-col sm:flex-row gap-2" data-testid="feed-milestone-add-row">
          {!creating ? (
            <Button
              size="sm"
              variant="outline"
              className="rounded-none h-8 text-xs w-full sm:w-auto"
              onClick={() => setCreating(true)}
              data-testid="feed-milestone-add-open"
            >
              <Plus className="w-3 h-3 mr-1" /> Add milestone
            </Button>
          ) : (
            <>
              <Input
                autoFocus
                placeholder="Milestone title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="h-8 text-xs rounded-none flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setCreating(false); setNewTitle(""); setNewDate(""); } }}
                data-testid="feed-milestone-add-title"
              />
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="h-8 text-xs rounded-none w-full sm:w-40"
                data-testid="feed-milestone-add-date"
              />
              <div className="flex items-center gap-1">
                <Button size="sm" className="rounded-none h-8 text-xs" onClick={submit} disabled={busy} data-testid="feed-milestone-add-confirm">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={() => { setCreating(false); setNewTitle(""); setNewDate(""); }} disabled={busy}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground py-4 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading milestones…</div>
      ) : upcoming.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 italic">No open milestones on this project.</div>
      ) : (
        <ul className="space-y-2" data-testid="feed-milestones-list">
          {upcoming.slice(0, 25).map((m) => {
            const overdue = m.due_date && m.due_date < today;
            const days = m.due_date ? Math.round((new Date(m.due_date).getTime() - Date.now()) / (24 * 3600 * 1000)) : null;
            return (
              <li
                key={m.id}
                className={`flex items-start gap-2 border-l-2 pl-3 py-1 ${overdue ? "border-rose-500 bg-rose-50/40" : "border-primary/60"}`}
                data-testid={`feed-milestones-${m.id}`}
              >
                <Flag className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${overdue ? "text-rose-600" : "text-primary"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{m.title || m.name || "Untitled milestone"}</div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    {m.due_date && (
                      <span className={overdue ? "text-rose-700 font-semibold" : ""}>
                        Due {m.due_date}{days != null && !overdue ? ` · in ${days}d` : ""}{overdue ? ` · ${Math.abs(days)}d overdue` : ""}
                      </span>
                    )}
                    {m.owner?.name && <span>· {m.owner.name}</span>}
                    {m.status && <span className="uppercase tracking-widest text-[9px] px-1 py-0.5 border border-border">{m.status}</span>}
                  </div>
                  {m.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{m.description}</div>}
                </div>
              </li>
            );
          })}
          {upcoming.length > 25 && (
            <li className="text-[10px] text-muted-foreground italic">+ {upcoming.length - 25} more not shown</li>
          )}
        </ul>
      )}
    </div>
  );
}



// R71 · Lightweight status-updates history — used by the Feed card's
// "Updates history" peek so directors can scroll past updates without
// leaving the Feed. R73 · Added thumbs up/down reactions on every update.
function ProjectUpdatesHistory({ projectId }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/status-updates`);
      setUpdates(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { setUpdates([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);
  if (loading) return <div className="text-xs text-muted-foreground py-4 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading updates…</div>;
  if (updates.length === 0) return <div className="text-xs text-muted-foreground py-4 italic">No status updates posted yet.</div>;
  return (
    <ul className="space-y-3" data-testid="feed-updates-history-list">
      {updates.map((u) => (
        <li key={u.id} className="border-l-2 border-primary/50 pl-3 py-1" data-testid={`feed-updates-history-${u.id}`}>
          <div className="text-[10px] text-muted-foreground mb-0.5">
            {u.author_name || "—"} · {fmtRelative(u.created_at)}
            {u.visibility && u.visibility !== "internal" && (
              <span className="ml-1 uppercase tracking-widest text-[9px] px-1 py-0.5 border border-border">{u.visibility}</span>
            )}
          </div>
          <div className="text-sm [&_p]:m-0 [&_ul]:my-1 [&_ol]:my-1">
            {u.body_html
              ? <SmartRichTextDisplay html={u.body_html} />
              : <span className="whitespace-pre-wrap">{u.body || ""}</span>}
          </div>
          <StatusUpdateReactions
            projectId={projectId}
            update={u}
            onChange={(next) => setUpdates((arr) => arr.map((x) => (x.id === next.id ? next : x)))}
          />
        </li>
      ))}
    </ul>
  );
}

// R73 · Thumbs up / thumbs down reactions on a status update. Directors
// use these to rate clarity/quality without opening a comment thread.
function StatusUpdateReactions({ projectId, update, onChange }) {
  const { user: me } = useAuth();
  const [busy, setBusy] = useState(false);
  const reactions = update.reactions || [];
  const up = reactions.filter((r) => r.kind === "up");
  const down = reactions.filter((r) => r.kind === "down");
  const mine = me?.id ? reactions.find((r) => r.user_id === me.id)?.kind || null : null;
  const react = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/status-updates/${update.id}/reactions`, { kind });
      // Accept both the in-place update pattern (History dialog passes
      // {...update, reactions}) and the refetch pattern (Feed card passes
      // a zero-arg loadProjects callback).
      if (typeof onChange === "function") {
        try { onChange({ ...update, reactions: data.reactions }); } catch { /* callback may not accept args */ }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reaction failed"));
    } finally {
      setBusy(false);
    }
  };
  const namesFor = (arr) => arr.map((r) => r.user_name).filter(Boolean).slice(0, 8).join(", ")
    + (arr.length > 8 ? ` +${arr.length - 8} more` : "");
  return (
    <div className="flex items-center gap-1 mt-1.5" data-testid={`feed-updates-reactions-${update.id}`}>
      <button
        type="button"
        onClick={() => react("up")}
        disabled={busy}
        className={`inline-flex items-center gap-1 h-6 px-1.5 text-[10px] border ${mine === "up" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-border bg-card text-muted-foreground hover:bg-muted/40"}`}
        title={up.length ? `Clear + ${namesFor(up)}` : "Rate clear / helpful"}
        data-testid={`feed-updates-reaction-up-${update.id}`}
      >
        <ThumbsUp className="w-3 h-3" />
        {up.length > 0 && <span>{up.length}</span>}
      </button>
      <button
        type="button"
        onClick={() => react("down")}
        disabled={busy}
        className={`inline-flex items-center gap-1 h-6 px-1.5 text-[10px] border ${mine === "down" ? "border-rose-500 bg-rose-50 text-rose-800" : "border-border bg-card text-muted-foreground hover:bg-muted/40"}`}
        title={down.length ? `Unclear / needs work · ${namesFor(down)}` : "Rate unclear / needs work"}
        data-testid={`feed-updates-reaction-down-${update.id}`}
      >
        <ThumbsDown className="w-3 h-3" />
        {down.length > 0 && <span>{down.length}</span>}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Intelligence view — health distribution + utilization + risk trends
// ═══════════════════════════════════════════════════════════════
function IntelligenceView({ projects }) {
  const total = projects.length;
  const health = { on_track: 0, at_risk: 0, off_track: 0, blocked: 0 };
  const byClient = new Map();
  const byBillingType = new Map();
  let onTimeCount = 0, closedWithDates = 0;
  projects.forEach((p) => {
    health[p.health || "on_track"] = (health[p.health || "on_track"] || 0) + 1;
    const cName = p.client?.name || p.client_name || "—";
    byClient.set(cName, (byClient.get(cName) || 0) + 1);
    byBillingType.set(p.billing_type || "unset", (byBillingType.get(p.billing_type || "unset") || 0) + 1);
    if (p.actual_end_date && p.planned_end_date) {
      closedWithDates += 1;
      if (p.actual_end_date <= p.planned_end_date) onTimeCount += 1;
    }
  });
  const onTimePct = closedWithDates ? Math.round(onTimeCount / closedWithDates * 100) : null;
  const topClients = Array.from(byClient.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const billingRows = Array.from(byBillingType.entries()).sort((a, b) => b[1] - a[1]);

  const stalest = projects
    .filter((p) => p.updated_at)
    .slice()
    .sort((a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""))
    .slice(0, 5);

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="projects-intelligence-view">
      <Panel title="Health distribution" icon={Activity}>
        <div className="space-y-1.5">
          {["on_track", "at_risk", "off_track", "blocked"].map((h) => {
            const n = health[h] || 0;
            const p = total ? Math.round(n / total * 100) : 0;
            return (
              <div key={h} className="flex items-center gap-2 text-xs">
                <div className="w-20">{HEALTH_LABEL[h]}</div>
                <div className="flex-1 h-3 bg-muted overflow-hidden">
                  <div className={`h-full ${HEALTH_BAR[h]}`} style={{ width: `${p}%` }} />
                </div>
                <div className="w-16 text-right font-mono">{n} <span className="text-muted-foreground">· {p}%</span></div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="On-time delivery" icon={onTimePct === null ? Clock : onTimePct >= 70 ? TrendingUp : TrendingDown}>
        {onTimePct === null ? (
          <div className="text-xs text-muted-foreground">No closed projects with planned + actual end dates yet.</div>
        ) : (
          <>
            <div className="text-4xl font-bold">{onTimePct}%</div>
            <div className="text-xs text-muted-foreground mt-1">{onTimeCount} of {closedWithDates} closed projects met the planned end date.</div>
          </>
        )}
      </Panel>

      <Panel title="Projects by client (top 6)" icon={FolderKanban}>
        <div className="space-y-1.5">
          {topClients.map(([name, n]) => {
            const p = total ? Math.round(n / total * 100) : 0;
            return (
              <div key={name} className="flex items-center gap-2 text-xs">
                <div className="w-40 truncate">{name}</div>
                <div className="flex-1 h-2 bg-muted overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${p}%` }} />
                </div>
                <div className="w-12 text-right font-mono">{n}</div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Billing mix" icon={Sparkles}>
        <div className="space-y-1.5">
          {billingRows.map(([b, n]) => {
            const p = total ? Math.round(n / total * 100) : 0;
            return (
              <div key={b} className="flex items-center gap-2 text-xs">
                <div className="w-24 capitalize">{b}</div>
                <div className="flex-1 h-2 bg-muted overflow-hidden">
                  <div className="h-full bg-sky-500" style={{ width: `${p}%` }} />
                </div>
                <div className="w-12 text-right font-mono">{n}</div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Stalest projects (no recent update)" icon={Clock} className="md:col-span-2">
        {stalest.length === 0 ? (
          <div className="text-xs text-muted-foreground">No data.</div>
        ) : (
          <div className="divide-y divide-border">
            {stalest.map((p) => (
              <Link key={p.id} to={`/app/projects/${p.id}`} className="flex items-center gap-3 py-2 hover:bg-muted/30" data-testid={`intel-stale-${p.id}`}>
                <HealthCell health={p.health} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{p.client?.name || p.client_name || "—"}</div>
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap">updated {fmtRelative(p.updated_at)}</div>
              </Link>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Panel({ title, icon: Icon, className = "", children }) {
  return (
    <div className={`border border-border p-4 ${className}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </div>
      {children}
    </div>
  );
}

function InlineText({ value, onSave, placeholder = "—", testid, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== (value || "")) onSave(draft.trim()); setEditing(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { if (draft !== (value || "")) onSave(draft.trim()); setEditing(false); }
          if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`h-7 w-full border border-primary bg-background px-1.5 text-xs rounded-none ${className || "font-mono"}`}
        data-testid={`${testid}-input`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`text-xs text-left w-full hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border truncate ${className || "font-mono"}`}
      data-testid={testid}
    >
      {value || <span className="text-muted-foreground italic">{placeholder}</span>}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Feb 2026 · Inline Status cell — click chip, pick a new status
// from a dropdown. Status catalogue comes from the workspace meta
// map so custom labels + colours surface here too.
// ═══════════════════════════════════════════════════════════════
function InlineStatus({ project, statusMeta, canEdit, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const s = (project.status || "active").toLowerCase();
  const meta = statusMeta[s] || { label: s.replace(/_/g, " "), cls: "border-primary/40 text-primary bg-primary/5" };
  const options = Object.entries(statusMeta).map(([k, v]) => ({ value: k, label: v?.label || k }));
  if (!canEdit) {
    return <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${meta.cls}`} data-testid={testid}>{meta.label}</span>;
  }
  if (editing) {
    return (
      <div className="min-w-[140px]" onClick={(e) => e.stopPropagation()}>
        <SearchableSelect
          value={s}
          onValueChange={(v) => { if (v && v !== s) onSave(v); setEditing(false); }}
          options={options}
          placeholder="— Status —"
          data-testid={`${testid}-select`}
          width="100%"
          triggerClassName="h-7 px-2 py-1 text-xs rounded-none border-primary"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`group text-[10px] uppercase tracking-widest border px-1.5 py-0.5 hover:ring-1 hover:ring-primary/40 transition ${meta.cls}`}
      title="Click to change status"
      data-testid={testid}
    >
      {meta.label}
      <Pencil className="inline-block w-2.5 h-2.5 ml-1 opacity-0 group-hover:opacity-70 transition" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Feb 2026 · Inline Date cell — click to reveal a native date
// input. Saves on change + blur. ESC reverts. Clearing fires
// `onSave("")` so the backend can null the field.
// ═══════════════════════════════════════════════════════════════
function InlineDate({ value, onSave, canEdit, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  if (!canEdit) {
    return <span className="font-mono text-xs" data-testid={testid}>{value || "—"}</span>;
  }
  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          type="date"
          value={draft || ""}
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            if (v !== (value || "")) onSave(v);
            setEditing(false);
          }}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
          className="h-7 border border-primary bg-background px-1 text-xs rounded-none font-mono"
          data-testid={`${testid}-input`}
        />
        {value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSave(""); setDraft(""); setEditing(false); }}
            className="text-[10px] text-muted-foreground hover:text-rose-600"
            data-testid={`${testid}-clear`}
          >
            clear
          </button>
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="group font-mono text-xs text-left w-full hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border"
      data-testid={testid}
    >
      {value || <span className="text-muted-foreground italic">—</span>}
      <Pencil className="inline-block w-2.5 h-2.5 ml-1 opacity-0 group-hover:opacity-70 transition" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Feb 2026 · Inline People Label cell — click to open a popover
// MultiSelect; save fires PATCH /projects/{id}/people-labels with
// the full map so the backend scrubs stale role IDs server-side.
// ═══════════════════════════════════════════════════════════════
function InlinePeopleLabel({ project, role, members, onSaved, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => project.people_labels?.[role.id] || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDraft(project.people_labels?.[role.id] || []);
  }, [project.id, project.people_labels, role.id]);

  const options = (members || [])
    .filter((m) => (m.status || "active") !== "removed" && (m.status || "active") !== "deleted")
    .map((m) => ({
      value: m.user_id || m.id,
      label: m.name || m.email || m.user?.name || m.user?.email || "User",
    }));

  const resolved = draft.map((uid) => {
    const m = members.find((x) => (x.user_id || x.id) === uid);
    const name = m?.name || m?.email || m?.user?.name || "User";
    const initials = name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
    return { uid, name, initials };
  });
  const extra = resolved.length > 3 ? resolved.length - 3 : 0;

  const save = async (next) => {
    const current = project.people_labels?.[role.id] || [];
    if (JSON.stringify(next) === JSON.stringify(current)) return;
    setBusy(true);
    try {
      const nextMap = { ...(project.people_labels || {}), [role.id]: next };
      // Strip empty arrays so the UI stays tidy.
      if (!next.length) delete nextMap[role.id];
      const { data } = await api.patch(`/projects/${project.id}/people-labels`, { labels: nextMap });
      onSaved?.({ id: project.id, people_labels: data.people_labels || nextMap });
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
      setDraft(current);
    } finally {
      setBusy(false);
    }
  };

  if (open) {
    return (
      <div className="min-w-[200px]" onClick={(e) => e.stopPropagation()}>
        <MultiSelect
          options={options}
          value={draft}
          onChange={(next) => { setDraft(next); save(next); }}
          placeholder={`Assign ${role.name}…`}
          allLabel={`Assign ${role.name}`}
          width="w-full"
          testid={`${testid}-select`}
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mt-1 text-[10px] text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-close`}
        >
          Close
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      className="group flex items-center gap-1 flex-wrap w-full text-left hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border transition"
      title={`Click to assign ${role.name}`}
      disabled={busy}
      data-testid={testid}
    >
      {resolved.length === 0 ? (
        <span className="text-muted-foreground text-xs italic">— assign —</span>
      ) : (
        <>
          {resolved.slice(0, 3).map((r) => (
            <span key={r.uid}
                   className={`inline-flex items-center gap-1 text-[10px] border px-1.5 py-0.5 ${role.color || "bg-muted/40 border-border"}`}>
              <span className="font-semibold">{r.initials}</span>
              <span className="truncate max-w-[12ch]">{r.name}</span>
            </span>
          ))}
          {extra > 0 && (
            <span className="text-[10px] border border-border px-1.5 py-0.5 bg-muted text-muted-foreground">
              +{extra}
            </span>
          )}
        </>
      )}
      <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition ml-auto shrink-0" />
    </button>
  );
}

// ─── Feb 2026 · Create project directly from Projects module ────

// Feb 2026 R292x · Bulk-action bar for the Projects list. Rendered by
// AdvancedTable right under the toolbar when ≥1 row is selected. Lets
// admins change status, archive, update health on several projects
// in one PATCH loop. Matches the subtle-outline UI of the Tasks bulk
// bar so there's one mental model across the app.
function ProjectsBulkActions({ rows, statusMeta, wsId, clients = [], brands = [], departments = [], members = [], onClear, onDone }) {
  const [busy, setBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkHealth, setBulkHealth] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const run = async (buildPatch, successMsg, { method = "patch", suffix = "" } = {}) => {
    if (!rows?.length) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(
        rows.map((r) => {
          const url = `/projects/${r.id}${suffix}`;
          if (method === "post") return api.post(url, buildPatch(r) ?? {});
          return api.patch(url, buildPatch(r));
        })
      );
      const okCount = results.filter((x) => x.status === "fulfilled").length;
      const failCount = results.length - okCount;
      if (okCount) toast.success(`${successMsg} · ${okCount} updated`);
      if (failCount) toast.error(`${failCount} failed — insufficient access or conflict`);
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk update failed"));
    } finally {
      setBusy(false);
    }
  };

  const applyStatus = () => {
    if (!bulkStatus) return;
    run(() => ({ status: bulkStatus }),
         `Status → ${statusMeta[bulkStatus]?.label || bulkStatus}`);
  };
  const applyHealth = () => {
    if (!bulkHealth) return;
    run(() => ({ health: bulkHealth }), `Health → ${bulkHealth}`);
  };
  const archive = () => {
    if (!window.confirm(`Archive ${rows.length} project${rows.length === 1 ? "" : "s"}?`)) return;
    run(() => null, "Archived", { method: "post", suffix: "/archive" });
  };
  const unarchive = () => run(() => null, "Unarchived", { method: "post", suffix: "/unarchive" });

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="projects-bulk-actions">
      <select
        value={bulkStatus}
        onChange={(e) => setBulkStatus(e.target.value)}
        disabled={busy}
        className="h-7 border border-border bg-background px-2 text-xs rounded-none"
        data-testid="projects-bulk-status-select"
      >
        <option value="">Change status…</option>
        {CANONICAL_STATUS_KEYS.map((k) => (
          <option key={k} value={k}>
            {statusMeta[k]?.label || DEFAULT_STATUS_META[k]?.label || k}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-none text-xs"
        disabled={!bulkStatus || busy}
        onClick={applyStatus}
        data-testid="projects-bulk-status-apply"
      >
        Apply status
      </Button>

      <select
        value={bulkHealth}
        onChange={(e) => setBulkHealth(e.target.value)}
        disabled={busy}
        className="h-7 border border-border bg-background px-2 text-xs rounded-none"
        data-testid="projects-bulk-health-select"
      >
        <option value="">Change health…</option>
        <option value="on_track">On track</option>
        <option value="at_risk">At risk</option>
        <option value="off_track">Off track</option>
      </select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-none text-xs"
        disabled={!bulkHealth || busy}
        onClick={applyHealth}
        data-testid="projects-bulk-health-apply"
      >
        Apply health
      </Button>

      <span className="w-px h-5 bg-border mx-1" />

      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-none text-xs"
        disabled={busy}
        onClick={archive}
        data-testid="projects-bulk-archive"
      >
        Archive
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-none text-xs"
        disabled={busy}
        onClick={unarchive}
        data-testid="projects-bulk-unarchive"
      >
        Unarchive
      </Button>

      <span className="w-px h-5 bg-border mx-1" />

      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-none text-xs"
        disabled={busy}
        onClick={() => setEditOpen(true)}
        data-testid="projects-bulk-edit-fields"
      >
        <Pencil className="w-3 h-3 mr-1" /> Edit field…
      </Button>

      <span className="ml-auto">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 rounded-none text-xs"
          onClick={onClear}
          data-testid="projects-bulk-clear"
        >
          Clear
        </Button>
      </span>

      <ProjectsBulkEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        count={rows.length}
        clients={clients}
        brands={brands}
        departments={departments}
        members={members}
        onApply={async (fieldKey, value) => {
          let v = typeof value === "string" ? value.trim() : value;
          if (v === "") v = fieldKey === "client_id" ? undefined : null;
          if (v === undefined) { toast.error("Client cannot be empty"); return; }
          const numericFields = ["budget", "estimated_hours", "actual_hours", "manual_effort_hours"];
          if (numericFields.includes(fieldKey) && v !== null) v = Number(v);
          await run(() => ({ [fieldKey]: v }), `${fieldKey} updated`);
          setEditOpen(false);
        }}
        saving={busy}
      />
    </div>
  );
}

// Feb 2026 R293zze · Shared bulk-edit dialog for the Projects list.
// Lets admins update one arbitrary field (priority, health, PM, brand,
// department, client, dates, budget, etc.) across every selected row.
function ProjectsBulkEditDialog({ open, onOpenChange, count, clients, brands, departments, members, onApply, saving }) {
  const [fieldKey, setFieldKey] = useState("");
  const [value, setValue] = useState("");

  const FIELDS = [
    { key: "priority",            label: "Priority",           kind: "select", options: ["low", "medium", "high", "urgent"] },
    { key: "health",              label: "Health",             kind: "select", options: ["on_track", "at_risk", "off_track", "blocked"] },
    { key: "billing_type",        label: "Billing type",       kind: "select", options: ["fixed", "hourly", "retainer"] },
    { key: "currency",            label: "Currency",           kind: "select", options: ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"] },
    { key: "budget",              label: "Budget",             kind: "number" },
    { key: "start_date",          label: "Start date",         kind: "date" },
    { key: "end_date",            label: "End date",           kind: "date" },
    { key: "planned_start_date",  label: "Planned start",      kind: "date" },
    { key: "planned_end_date",    label: "Planned end",        kind: "date" },
    { key: "client_id",           label: "Client",             kind: "lookup", options: clients.map((c) => ({ value: c.id, label: c.name })) },
    { key: "brand_id",            label: "Brand",              kind: "lookup", options: brands.map((b) => ({ value: b.id, label: b.name })) },
    { key: "department_id",       label: "Department",         kind: "lookup", options: departments.map((d) => ({ value: d.id, label: d.name })) },
    { key: "project_manager_id",  label: "Project manager",    kind: "lookup", options: members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email || m.user_id })) },
    { key: "sponsor_id",          label: "Sponsor",            kind: "lookup", options: members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email || m.user_id })) },
  ];
  const col = FIELDS.find((f) => f.key === fieldKey);

  const reset = () => { setFieldKey(""); setValue(""); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="projects-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} project{count === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold mb-1">Field</div>
            <Select value={fieldKey} onValueChange={(v) => { setFieldKey(v); setValue(""); }}>
              <SelectTrigger className="rounded-none h-9" data-testid="projects-bulk-field-select">
                <SelectValue placeholder="Choose a field…" />
              </SelectTrigger>
              <SelectContent>
                {FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {col && (
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold mb-1">New value</div>
              {col.kind === "select" ? (
                <select
                  value={value || ""}
                  onChange={(e) => setValue(e.target.value)}
                  className="h-9 rounded-none text-sm border border-border bg-background w-full px-2"
                  data-testid={`projects-bulk-value-${col.key}`}
                >
                  <option value="">— (clear)</option>
                  {col.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                </select>
              ) : col.kind === "lookup" ? (
                <SearchableSelect
                  value={value}
                  onValueChange={setValue}
                  options={col.options}
                  placeholder="—"
                  clearable
                  width="100%"
                  data-testid={`projects-bulk-value-${col.key}`}
                />
              ) : (
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  type={col.kind === "number" ? "number" : col.kind === "date" ? "date" : "text"}
                  className="h-9 rounded-none text-sm"
                  data-testid={`projects-bulk-value-${col.key}`}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none"
                     data-testid="projects-bulk-cancel-btn">Cancel</Button>
          <Button
            disabled={saving || !fieldKey}
            onClick={() => onApply(fieldKey, value)}
            className="rounded-none"
            data-testid="projects-bulk-apply-btn"
          >
            {saving ? "Applying…" : `Apply to ${count}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function NewProjectDialog({ clients = [], brands = [], departments = [], onClose, onCreated }) {
  const { activeWorkspaceId } = useAuth();
  const { modules } = useWorkspacePerms();
  const brandsOn = modules?.brands !== false;
  const { statusMeta } = useProjectStatuses(activeWorkspaceId);
  const [clientId, setClientId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // Feb 2026 R292v · Pick project status at creation. Defaults to
  // "active" so one-click create keeps working; respects per-workspace
  // overrides so a renamed "Active → Live" label shows the right text.
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);

  const brandOptions = useMemo(() => {
    if (!clientId) return [];
    // Brands filtered to selected client (or brands with no client_id)
    return brands.filter((b) => !b.client_id || b.client_id === clientId);
  }, [brands, clientId]);

  const clientOptions = useMemo(
    () => [...clients]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((c) => ({ value: c.id, label: c.name, hint: c.client_code || undefined })),
    [clients],
  );

  const brandDropdownOptions = useMemo(
    () => brandOptions
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((b) => ({ value: b.id, label: b.name })),
    [brandOptions],
  );

  const departmentDropdownOptions = useMemo(
    () => [...departments]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((d) => ({ value: d.id, label: d.name, hint: d.code || undefined })),
    [departments],
  );

  const submit = async () => {
    if (!name.trim()) { toast.error("Project name is required"); return; }
    setBusy(true);
    try {
      // R138d · Feb 2026 — Client is optional. When picked we use the
      // client-scoped endpoint; when blank we hit the workspace-scoped
      // one so internal / R&D / ops projects can be created.
      const url = clientId
        ? `/clients/${clientId}/projects`
        : `/workspaces/${activeWorkspaceId}/projects`;
      const { data } = await api.post(url, {
        name: name.trim(),
        client_id: clientId || null,
        description: description.trim() || null,
        brand_id: brandId || null,
        department_id: departmentId || null,
        planned_start_date: start || null,
        planned_end_date: end || null,
        status: status || "active",
      });
      invalidateLookup("projects", activeWorkspaceId);
      onCreated?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <FolderKanban className="w-4 h-4 text-primary" /> New project
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold block mb-1">Client <span className="text-muted-foreground font-normal">(optional)</span></label>
            <SearchableSelect
              value={clientId}
              onValueChange={(v) => { setClientId(v); setBrandId(""); }}
              options={clientOptions}
              placeholder="Search clients…  ·  leave blank for internal projects"
              clearable
              triggerClassName="h-10 text-sm w-full"
              width="100%"
              data-testid="new-project-client"
            />
          </div>
          {brandsOn && (
            <div>
              <label className="text-xs font-semibold block mb-1">Brand (optional)</label>
              <SearchableSelect
                value={brandId}
                onValueChange={setBrandId}
                options={brandDropdownOptions}
                placeholder={clientId ? "Search brands…" : "Pick a client first"}
                disabled={!clientId}
                triggerClassName="h-10 text-sm w-full"
                width="100%"
                data-testid="new-project-brand"
              />
              {clientId && brandDropdownOptions.length === 0 && (
                <p className="text-[10px] text-muted-foreground italic mt-1">This client has no brands yet — you can leave it blank.</p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-semibold block mb-1">Department <span className="text-muted-foreground font-normal">(optional)</span></label>
            <SearchableSelect
              value={departmentId}
              onValueChange={setDepartmentId}
              options={departmentDropdownOptions}
              placeholder={departmentDropdownOptions.length ? "Search departments…" : "No departments yet — create one under HR & Org"}
              disabled={departmentDropdownOptions.length === 0}
              clearable
              triggerClassName="h-10 text-sm w-full"
              width="100%"
              data-testid="new-project-department"
            />
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Project name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Q3 GTM Campaign"
                    className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none"
                    data-testid="new-project-name"
                    autoFocus />
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Description (optional)</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                       className="w-full border border-border bg-background px-2 py-1 text-sm rounded-none"
                       data-testid="new-project-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold block mb-1">Planned start</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                      className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none"
                      data-testid="new-project-start" />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1">Planned end</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                      className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none"
                      data-testid="new-project-end" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Starting status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none"
              data-testid="new-project-status"
            >
              {CANONICAL_STATUS_KEYS.map((k) => (
                <option key={k} value={k}>
                  {statusMeta[k]?.label || DEFAULT_STATUS_META[k]?.label || k}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground italic mt-1">
              Workspace-level renames apply automatically. Change this on the Project Overview any time.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border mt-3">
          <button className="text-xs px-3 py-1.5 border border-border hover:bg-muted" onClick={onClose}>Cancel</button>
          <button className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50 inline-flex items-center gap-1.5" onClick={submit} disabled={busy || !name.trim()} data-testid="new-project-save">
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Creating…</> : "Create project"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

